import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';
import { read, utils, writeFile } from 'xlsx';
import _ from 'lodash';
import Fuse from 'fuse.js';
import { Dialog } from 'primereact/dialog';

//helpers
import Messages from '../../helpers/Messages';
import { Button, CheckboxField } from '../../helpers/';

//common components
import Loading from '../../common/Loading';
import useUnsavedChangesWarning from '../../common/hooks/useUnsavedChangesWarning';
import AddNewCompany from './components/AddNewCompany';
import AddShippingAddress from '../../common/AddShippingAddress';
import ReqDetailsModal from '../NewRequirementBuyer/Components/ReqDetailsModal';

//APIs
import { getADHOCCompanies, getRCCompanies, getSTOCKCompanyBranches } from '../../services/api/company';
import { searchUserRoles } from '../../services/api/user';
import { getBranchesList, getCompanyBranchById } from '../../services/api/companyBranch';
import { getCompanyBillAddrs } from '../../services/api/companyBillAddr';
import { addCompanyBranchShipAddrData, getCompanyBranchShipAddrs } from '../../services/api/companyBranchShipAddr';
import { addNewCompanyFromBuyerReq, getBuyerRequirementsById, getReqDetailsForIncrementalReqByBuyerReqId, insertBuyerRequirement } from '../../services/api/buyerReq';
import { getBuyerRateContractByCompanyIdAndBranchId, getVMIBuyerRateContractByCompanyIdAndBranchId } from '../../services/api/buyerRateContracts';
import { getOrderTypesData } from '../../services/api/orderTypes';
import { getCompanyERPCodeMapByCompanyId, getVMICompanyERPCodeMapByCompanyId } from '../../services/api/companyERPCodeMap';
import { getMaterialMasterAndTempMaterialIds, getMaterialMasterAndTempMaterialIdsById } from '../../services/api/materialMaster';
import { getReqTags } from '../../services/api/reqTags';
import { getCompanyGSTINs } from '../../services/api/companyGSTINs';
import { raiseToast } from '../../services/redux/reducers/toast/toast';
import { getBuyerPOsByMaterialIds } from '../../services/api/buyerPO';
import { fetchCountries, fetchMaterialBrands, fetchMaterialCategories, fetchMaterialUoms, fetchStates, fetchPinCodes, fetchPMBranches } from '../../services/redux/reducers/commonThunk';
import requirement, { updateBuyerRequirementData, updateBuyerRequirementItemsData } from '../../services/redux/reducers/Requirement/requirement';
import { getPendingBuyerReq } from '../../services/api/buyerReq';
import { insertIncrementalReqLog, searchIncrementalReqLogs } from '../../services/api/incrementalReqLogs';

//child components
import RateContractSearch from './components/RateContractSearch';
import MaterialSearch from './components/MaterialSearch';
import PendingBuyerReq from './components/PendingBuyerReq';
import EmailRecipientDetails from './components/EmailRecipientDetails';
import "./index.css";

//Utilities
import { checkFileType, getFullName, getProductName, getUploadedFileObj, round, sortArrayOfObjectsInAlphabeticalOrder, validateInputs } from '../../utils';

//Validations
import { PostRequirementSchem, postRequirementInitialState } from '../../validation/postRequirementValidation';
import ProductsSection from './components/ProductsSection';
import RequirementDetails from './components/RequirementDetails';
import AddressSelection from './components/AddressSelection';
import { getCompanyById } from '../../services/api/company';
import { getUserById } from '../../services/api/user';
import { getPMCompanyConfig } from '../../setup/pmCompanyConfig';
import { getVMICompanies } from '../../services/api/vmiContractItems';


const bussinessTypes = [
    {
        bussinessTypeId: 1,
        bussinessType: 'REGULAR'
    },
    {
        bussinessTypeId: 2,
        bussinessType: 'VMI'
    }
]


const PostRequirement = () => {
    const location = useLocation()

    //requirement
    const [requirement, setRequirement] = useState(postRequirementInitialState)
    const [orderTypes, setOrderTypes] = useState([])
    const [companies, setCompanies] = useState([])
    const [allCompanies, setAllCompanies] = useState([])
    const [companySuggestions, setCompanySuggestions] = useState([])
    const [branches, setBranches] = useState([])
    const [warehouses, setWarehouses] = useState([])
    const [pmBranches, setPMBranches] = useState([])
    const [gstins, setGstins] = useState([])
    const [users, setUsers] = useState([])
    const [shippingAddresses, setShippingAddressesList] = useState([])
    const [billingAddresses, setBillingAddressesList] = useState([])
    const [reqTags, setReqtags] = useState([])
    const [formErrors, setFormErrors] = useState([])
    const [pendingReqList, setPendingReqList] = useState([])
    const [buyerPO, setBuyerPO] = useState({})
    const [incrementalReqLogs, setIncrementalReqLogs] = useState([])
    const [incrementalReqProducts, setIncrementalReqProducts] = useState([])

    // product
    const [products, setProducts] = useState([])
    const [rateContractProducts, setRateContractProducts] = useState([])
    const [companyERPCodeMaps, setCompanyERPCodeMaps] = useState([])
    const [activeTab, setActiveTab] = useState(0)

    //Modals
    const [visibleAddNewCompany, setVisibleAddNewCompany] = useState(false)
    const [visibleAddNewShippingAddr, setVisibleAddNewShippingAddr] = useState(false)
    const [visisbleAddRCProducts, setVisisbleAddRCProducts] = useState(false)
    const [visisbleAddAdhocProducts, setVisisbleAddAdhocProducts] = useState(false)
    const [visiblePendingReqModel, setVisiblePendingReqModel] = useState(false)
    const [visibleAutomateRCModal, setVisibleAutomateRCModal] = useState(false)
    const [visibleAdhocEmailRecipientDetails, setVisibleAdhocEmailRecipientDetails] = useState(false)
    const [isIncrementalReqConfirmModalVisible, setIsIncrementalReqConfirmModalVisible] = useState(false)

    //Misc
    const [setEdit, setFinished] = useUnsavedChangesWarning(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isCompanyDataLoading, setIsCompanyDataLoading] = useState(false)
    const [isBranchDataLoading, setIsBranchDataLoading] = useState(false)
    const [isUsersDataLoading, setIsUsersDataLoading] = useState(false)
    const [isGSTINSDataLoading, setIsGSTINSDataLoading] = useState(false)
    const [isCompanyERPCodeMapsLoading, setIsCompanyERPCodeMapsLoading] = useState(false)
    const [isRateContractProductsLoading, setIsRateContractProductsLoading] = useState(false)

    const pmCompanyDetails = useSelector((state) => state.userDetails.PMCompanyDetails)
    const rolesList = useSelector((state) => state.userDetails.rolesList)
    const activeRoleId = useSelector((state) => state.userDetails.activeRoleId)
    const states = useSelector((state) => state.common.states)
    const brandsData = useSelector((state) => state.common.materialBrands)
    const industryData = useSelector((state) => state.common.materialCategories)
    const uomsData = useSelector((state) => state.common.materialUOMs)
    const countriesData = useSelector((state) => state.common.countries)
    const pinCodes = useSelector(state => state.common.pinCodes)
    const companyPMBranches = useSelector(state => state.common.PMBranches)

    const buyerRequirementData = useSelector(state => state.requirements.buyerRequirementData)
    const buyerRequirementItemsData = useSelector(state => state.requirements.buyerRequirementItemsData)
    const userNameDetails = useSelector(state => state.userDetails.userNameDetails)
    const userDetails = useSelector(state => state.userDetails)
    const materialUOMs = useSelector(state => state.common.materialUOMs)
    const materialCategories = useSelector(state => state.common.materialCategories)
    const materialBrands = useSelector(state => state.common.materialBrands)

    const PMCompanyConfig = getPMCompanyConfig(userDetails.PMCompanyDetails.companyId)

    const activeRole = userDetails?.rolesList?.filter(eachRole => eachRole?.userRoleId === userDetails?.activeRoleId)?.[0]

    let buyerReqId = null
    
    const queryParams = new URLSearchParams(location.search)
    buyerReqId = queryParams.get('buyerReqId')

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const companyFuzzSearch = useCallback(new Fuse(companies, {
        keys: ['companyName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true,
        getFn: (obj) => {
            return obj.companyName.replace(/\s+/g, '')
        },
    }), [companies])

    useEffect(() => {
        setIsLoading(true)
        dispatch(fetchPinCodes())
        dispatch(fetchMaterialCategories())
        dispatch(fetchMaterialBrands())
        dispatch(fetchMaterialCategories())
        dispatch(fetchMaterialUoms())
        dispatch(fetchStates())
        dispatch(fetchCountries())
        dispatch(fetchPMBranches())

        getInitialData()
    }, [])

    useEffect(() => {
        if (buyerReqId && materialCategories.length && materialBrands.length && materialUOMs.length){
            constructBuyerReq()
        }
    }, [materialCategories, materialUOMs, materialBrands])

    useEffect(() => {
        if (pmCompanyDetails?.companyBranchShipAddrs?.length > 0 && rolesList.length > 0 && activeRoleId) {
            prefillWarehouseData()
            prefillPMBranches()
        }

    }, [pmCompanyDetails, rolesList, activeRoleId, companyPMBranches])

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)
        buyerReqId = queryParams.get('buyerReqId')
        if (!buyerReqId){
            if (branches.length || gstins.length || users.length || shippingAddresses.length || billingAddresses.length) {
                setBranches([])
                setGstins([])
                setUsers([])
                setShippingAddressesList([])
                setBillingAddressesList([])
            }

            if (requirement.orderTypeId === 3) {
                const companyDetails = [{ companyId: pmCompanyDetails.companyId, companyName: pmCompanyDetails.companyName, tags: pmCompanyDetails.tags }]
                setCompanies(companyDetails)

                setProducts([])

                setRequirement({ ...requirement, pmWarehouseId: null })
                setActiveTab(1)
            }
            else if (requirement.orderTypeId === 1 || requirement.orderTypeId === 2) {
                const activePMCompanyDetails = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)
                const companyData = { prefPMBranchIds: [activePMCompanyDetails?.branchId ? activePMCompanyDetails?.branchId : 1], sortBy: 'companyName', sortOrder: 'ASC', buyerSeller: [1, 3], paginator: false, statusId: 1 }
                getCompanyData(companyData)

                setProducts([])
                setRateContractProducts([])
                setCompanyERPCodeMaps([])

                setActiveTab(0)
            }
        }
    }, [requirement.orderTypeId])

    const prefillWarehouseData = () => {
        const activeRole = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)

        const relatedCompanyBranch = pmCompanyDetails.companyBranches.find(eachBranch => eachBranch.branchId === activeRole?.branchId)

        let relatedCompanyWarehouse = null
        if (relatedCompanyBranch?.primaryShipAddrId) {
            relatedCompanyWarehouse = pmCompanyDetails.companyWarehouses.find(eachWarehouse => eachWarehouse.shipAddrId === relatedCompanyBranch?.primaryShipAddrId)
        }

        if (relatedCompanyWarehouse) {
            const updatedRequirement = { ...postRequirementInitialState, pmWarehouseId: relatedCompanyWarehouse?.warehouseId }
            setRequirement(updatedRequirement)
        }

        const companyWarehouses = sortArrayOfObjectsInAlphabeticalOrder(pmCompanyDetails.companyWarehouses, 'warehouseName')
        setWarehouses(companyWarehouses)
    }

    const prefillPMBranches = () => {
        const activeRole = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)
        let relatedBranches = []
        if (activeRole.branchId === 0) {
            relatedBranches = pmCompanyDetails.companyBranches
        }
        else {
            relatedBranches = pmCompanyDetails.companyBranches.filter(eachBranch => eachBranch.branchId === activeRole.branchId)
        }
        relatedBranches = relatedBranches.map(eachBranch => {
            const relatedCompanyPMBranch = companyPMBranches.find(eachCompanyPMBranch => eachCompanyPMBranch.branchId === eachBranch.branchId)
            if(relatedCompanyPMBranch){
                return {...eachBranch, warehouseId: relatedCompanyPMBranch.warehouseId}
            }
            return {...eachBranch, warehouseId: null}
        })

        setPMBranches(relatedBranches)
        if(relatedBranches.length === 1){
            setRequirement(prev => ({ ...prev, pmBranchId: relatedBranches[0].branchId }))
        }
    }

    //All API calls for requirement

    const constructBuyerReq = async () => {
        setIsLoading(true)

        const buyerReqDetailsRes = await getReqDetailsForIncrementalReqByBuyerReqId(buyerReqId)
        if (buyerReqDetailsRes.failure){
            return dispatch(raiseToast({ severity: 'error', summary: 'Buyer Req Details', detail: buyerReqDetailsRes.data.error }))
        }

        const buyerReq = buyerReqDetailsRes.data.buyerReq
        const buyerReqItems = buyerReqDetailsRes.data.buyerReqItems
        const companies = buyerReqDetailsRes.data.companies
        const companyBranches = buyerReqDetailsRes.data.companyBranches
        let companyGSTINs = buyerReqDetailsRes.data.companyGSTINs
        const companyBillAddrs = buyerReqDetailsRes.data.companyBillAddrs
        const companyBranchShipAddrs = buyerReqDetailsRes.data.companyBranchShipAddrs
        const materialDetails = buyerReqDetailsRes.data.materialDetails
        const buyerPO = buyerReqDetailsRes.data.buyerPO

        let buyerUserDetails = []
        buyerReqDetailsRes.data.userDetails.map(eachUser => {
            buyerUserDetails.push({...eachUser, fullName: getFullName(eachUser.firstName, eachUser.lastName)})
        })

        if (buyerReq.isURP){
            const URPGSTIN = { gstinId: uuidv4(), gstinCode: 'URP' }
            companyGSTINs = [...companyGSTINs, URPGSTIN]
            
            buyerReq.gstinId = URPGSTIN.gstinId
        }else{
            const selectedBillAddr = companyBillAddrs.find(eachCompanyBillAddr => eachCompanyBillAddr.billAddrId === buyerReq.billAddrId)
            const relatedBranchGSTIN = companyGSTINs.find(eachCompanyGSTIN => eachCompanyGSTIN.gstinCode === selectedBillAddr.gstinCode)

            buyerReq.gstinId = relatedBranchGSTIN.gstinId
        }

        let updatedBuyerRequirement = {
            ...buyerReq, 
            bussinessTypeId: buyerReq.reqTagId === 4 ? 2 : 1,
            isRouted: buyerReq.isRouted ? "Yes" : "No",
            reqSourceDoc: null,
            requirementDate: new Date(buyerReq.requirementDate),
            prevClientReqNo: buyerReq.clientReqNo,
            clientReqNo: null
        }

        if (updatedBuyerRequirement.orderTypeId === 2 && updatedBuyerRequirement.bussinessTypeId === 1) {
            const rateContractCompanyDetails = { companyId: updatedBuyerRequirement.companyId, branchIds: [updatedBuyerRequirement.branchId, 0], warehouseIds: [updatedBuyerRequirement.pmWarehouseId] }
            getBuyerRateContractDetails(rateContractCompanyDetails, updatedBuyerRequirement)
        }

        let products = []
        buyerReqItems.map(eachBuyerReqItem => {
            const relatedMaterial = materialDetails.find(eachMaterial => eachMaterial.buyerReqItemId === eachBuyerReqItem.buyerReqItemId)
            const relatedMaterialUOM = materialUOMs.find(eachMaterialUOM => eachMaterialUOM.UOM === relatedMaterial.UOM)
            const relatedIncrementalReqLog = buyerReqDetailsRes.data.incrementalReqLogs.find(eachIncrementalReqLog => eachIncrementalReqLog.materialId === eachBuyerReqItem.materialId)
            const relatedRateContractProduct = rateContractProducts.find(eachRateContractProduct => eachRateContractProduct.materialId === eachBuyerReqItem.materialId)
            
            let newProduct = {
                productId: uuidv4(),
                buyerReqItemId: eachBuyerReqItem.buyerReqItemId,
                materialId: eachBuyerReqItem.materialId,
                productName: relatedMaterial.productName,
                specification: relatedMaterial.productSpecification,
                brandId: relatedMaterial.brandId,
                categoryId: relatedMaterial.categoryId,
                uomValue: relatedMaterial.UOMValue,
                materialUOMId: relatedMaterialUOM.materialUOMId,
                category: relatedMaterial.categoryId,
                brand: relatedMaterial.brandId,
                brandRCName: relatedMaterial.brandId,
                existedQuantity: relatedIncrementalReqLog ? relatedIncrementalReqLog.qty : eachBuyerReqItem.qty,
                quantity: relatedIncrementalReqLog ? relatedIncrementalReqLog.qty : eachBuyerReqItem.qty,
                companyERPCode: relatedMaterial.companyERPCode,
                variantName: relatedMaterial.variantName,
                isMappedProduct: relatedMaterial.companyERPCodeMapId ? true : false,
                buyerRateContractId: relatedRateContractProduct ? relatedRateContractProduct.buyerRateContractId : null,
                companyERPCodeMapId: relatedMaterial.companyERPCodeMapId,
                // isOverRiddedProduct: false,
                // isMaterialMasterProduct: false
            }
            products.push(newProduct)
        })

        setRequirement(updatedBuyerRequirement)
        setProducts(products)
        setBuyerPO(buyerPO)
        setAllCompanies(companies)
        setCompanies(companies)
        setBranches(companyBranches)
        setGstins(companyGSTINs)
        setBillingAddressesList(companyBillAddrs)
        setShippingAddressesList(companyBranchShipAddrs)
        setUsers(prevState => ([...prevState, ...buyerUserDetails]))
        setIncrementalReqLogs(buyerReqDetailsRes.data.incrementalReqLogs)
        setIsLoading(false)
    }

    const getInitialData = async () => {
        const orderTypeResponse = await getOrderTypesData()
        if (orderTypeResponse.failure) {
            return dispatch(raiseToast({ severity: 'error', summary: 'Order Types', detail: orderTypeResponse.data.error, life: 3000 }))
        }
        else {
            setOrderTypes(orderTypeResponse.data)
        }

        const reqTagsResponse = await getReqTags()
        if (reqTagsResponse.failure) {
            return dispatch(raiseToast({ severity: 'error', summary: 'Requirement Types', detail: reqTagsResponse.data.error, life: 3000 }))
        }
        else {
            setReqtags(reqTagsResponse?.data)
        }
        setIsLoading(false)
    }

    const getCompanyData = async (companyData) => {
        setIsCompanyDataLoading(true)

        let response = {}
        if(requirement.bussinessTypeId === 1){
            if (requirement.orderTypeId === 2) {
                response = await getRCCompanies({ ...companyData })
            }
            else {
                response = await getADHOCCompanies({ ...companyData })
            }

            if (response.failure) {
                dispatch(raiseToast({ severity: 'error', summary: 'Company', detail: response.data.error, life: 3000 }))

                setCompanies([]) //Empty the compnies in case of errors(e)
                setIsCompanyDataLoading(false)
            }

            if (response?.data?.data?.length === 0) {
                dispatch(raiseToast({ severity: 'error', detail: "No companies are assigned to the user", life: 3000 }))
            }

            const addCompanyOption = {
                companyName: 'Add New company',
                companyId: 0,
            }

            let updatedCompanyData = _.uniqBy(response?.data?.data, "companyId")
            updatedCompanyData = sortArrayOfObjectsInAlphabeticalOrder(updatedCompanyData, 'companyName')
            updatedCompanyData = requirement.orderTypeId === 1 ? [addCompanyOption, ...updatedCompanyData] : [...updatedCompanyData]

            updatedCompanyData = updatedCompanyData.filter(eachCompany => eachCompany.companyId !== pmCompanyDetails.companyId)

            setCompanies(updatedCompanyData)
            setAllCompanies(response?.data?.data) //For branch display

            setIsCompanyDataLoading(false)
        }
        else if(requirement.bussinessTypeId === 2){
            response = await getVMICompanies({...companyData})
            if(response.failure){
                dispatch(raiseToast({ severity: 'error', summary: 'Company', detail: response.data.error, life: 3000 }))
            }

            if (response.length === 0) {
                dispatch(raiseToast({ severity: 'error', detail: "No companies in VMI", life: 3000 }))
            }

            let updatedCompanyData = _.uniqBy(response, "companyId")
            updatedCompanyData = sortArrayOfObjectsInAlphabeticalOrder(updatedCompanyData, 'companyName')

            updatedCompanyData = updatedCompanyData.filter(eachCompany => eachCompany.companyId !== pmCompanyDetails.companyId)

            setCompanies(updatedCompanyData)
            setAllCompanies(response)

            setIsCompanyDataLoading(false)
        }
    }

    const getBranchData = async (companyId) => {
        setIsBranchDataLoading(true)
        const activePMCompanyDetails = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)

        let response = {}
        if (requirement.orderTypeId === 3) {
            const data = { companyId: companyId, prefPMBranchIds: [activePMCompanyDetails?.branchId ? activePMCompanyDetails?.branchId : 1] }
            response = await getSTOCKCompanyBranches(data)
        } else {
            const data = { companyIds: companyId, sortBy: 'branchName', sortOrder: "ASC", prefPMBranchIds: [activePMCompanyDetails?.branchId ? activePMCompanyDetails?.branchId : 1], paginator: false, statusId: 1 }
            response = await getBranchesList(data)
        }

        if (response?.failure) {
            dispatch(raiseToast({ severity: 'error', summary: 'Branches', detail: response.data.error, life: 3000 }))
            setIsBranchDataLoading(false)
        }
        const branchesData = sortArrayOfObjectsInAlphabeticalOrder(response.data.data, 'branchName')
        setBranches(branchesData)
        setIsBranchDataLoading(false)
    }

    const getUserData = async (data) => {
        setIsUsersDataLoading(true)
        const response = await searchUserRoles({ ...data })

        if (response.failure) {
            setIsUsersDataLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: 'users', detail: response.data.error, life: 3000 }))
        }
        else {
            let usersList = response.data.data.map(eachUser => {
                return {
                    userId: eachUser.userId,
                    fullName: eachUser["user.firstName"] + " " + (eachUser?.["user.lastName"] ? eachUser["user.lastName"] : '')
                }
            })

            usersList = _.uniqBy(usersList, 'userId');
            usersList = sortArrayOfObjectsInAlphabeticalOrder(usersList, 'fullName')


            setUsers(usersList)
            setIsUsersDataLoading(false)
            return usersList
        }
    }

    const getBillingAddressData = async (data) => {
        const response = await getCompanyBillAddrs({ ...data })
        if (response.failure) return dispatch(raiseToast({ severity: 'error', summary: 'Billing Address', detail: response.data.error, life: 3000 }))

        if (response.data.data.length === 0) {
            dispatch(raiseToast({ severity: 'error', detail: "Billing addressses are not available. Please contact tech support", life: 3000 }))
        }

        setBillingAddressesList(response.data.data)
    }

    const getShippingAddressData = async (data) => {
        const response = await getCompanyBranchShipAddrs({ ...data })
        if (response.failure) return dispatch(raiseToast({ severity: 'error', summary: 'Shipping Address', detail: response.data.error, life: 3000 }))

        setShippingAddressesList(response.data.data)
    }

    const getCompanyERPCodeMapData = async (data) => {
        setIsCompanyERPCodeMapsLoading(true)

        let response = null
        if(requirement.bussinessTypeId === 2){
            response = await getVMICompanyERPCodeMapByCompanyId(data)
        }
        else{
            response = await getCompanyERPCodeMapByCompanyId(data)
        }

        if (response.failure) {
            setIsCompanyERPCodeMapsLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: 'Company ERP codes', detail: response.data.error, life: 3000 }))
        }
        else {
            const modifiedCompanyERPCodeMapData = response?.data.map(eachERP => {
                const prodUOM = uomsData.find(eachUOM => eachUOM.UOM === eachERP.UOM)
                return { ...eachERP, categoryId: eachERP?.categoryId, materialUOMId: prodUOM?.materialUOMId }
            })

            setCompanyERPCodeMaps(modifiedCompanyERPCodeMapData)
            setIsCompanyERPCodeMapsLoading(false)
        }
    }

    const getBuyerRateContractDetails = async (rateContractCompanyDetails, buyerReq) => {
        const updatedRequirement = buyerReq ? buyerReq : requirement

        setIsRateContractProductsLoading(true)
        let response = null 
        if(updatedRequirement.bussinessTypeId === 1){
            response = await getBuyerRateContractByCompanyIdAndBranchId({ ...rateContractCompanyDetails })
        }
        else if (updatedRequirement.bussinessTypeId === 2){
            response = await getVMIBuyerRateContractByCompanyIdAndBranchId({ ...rateContractCompanyDetails })
        }

        if (response.failure) {
            setIsRateContractProductsLoading(false)
            dispatch(raiseToast({ severity: 'error', summary: 'Rate Contract Products', detail: response.data.error, life: 3000 }))
        } else {

            const modifiedRateContractsList = response.data.map(eachProduct => {
                const erpCode = eachProduct.companyERPCode ? `${eachProduct.companyERPCode} |` : ''
                const productString = `${eachProduct.materialId} | ${erpCode} ${eachProduct.companyProdName} | ${eachProduct.companyProdSpec}`

                const materialUOM = uomsData.find(eachUOM => eachUOM.UOM === eachProduct.UOM)
                const relatedBrand = brandsData.find(eachBrand => eachBrand.brandId === eachProduct.brandId)
                const relatedCategory = industryData.find(eachCategory => eachCategory.categoryId === eachProduct.categoryId)

                const updatedUOMValue = !eachProduct.UOMValue && eachProduct.materialId.toString().length >= 7 ? 1 : eachProduct.UOMValue

                return {
                    ...eachProduct,
                    rateContractProductId: uuidv4(),
                    productString,
                    materialUOMId: materialUOM?.materialUOMId,
                    brandName: relatedBrand?.brandName,
                    categoryName: relatedCategory?.categoryName,
                    UOMValue: updatedUOMValue
                }
            })
            setRateContractProducts(modifiedRateContractsList)
            setIsRateContractProductsLoading(false)
        }
    }

    const getCompanyGstinList = async (data) => {
        setIsGSTINSDataLoading(true)

        const companyGSTINsRes = await getCompanyGSTINs({ ...data })
        if (companyGSTINsRes?.failure) {
            setIsGSTINSDataLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: 'companyGSTINsRes', detail: companyGSTINsRes.data.error, life: 3000 }))
        }

        if (companyGSTINsRes?.data?.data?.length === 0) {
            const gstinsData = [{ gstinId: uuidv4(), gstinCode: 'URP' }] //If there were no gstins for the companyBranches, insert URP as gstins
            setGstins(gstinsData)

            getBillingAddressData({ companyIds: [requirement.companyId], gstinCodes: ['URP'], sortBy: 'billAddress', paginator: false, statusIds: [1] })
        }
        else {
            setGstins(companyGSTINsRes?.data?.data)
        }
        setIsGSTINSDataLoading(false)
    }

    //All event handlers requirement

    const handleBussinessType = (event) => {
        setEdit()

        setProducts([])
        setRateContractProducts([])
        setCompanyERPCodeMaps([])
        setCompanies([])
        setActiveTab(0)

        setRequirement({ 
            ...postRequirementInitialState, 
            bussinessTypeId: event.target.value, 
            pmWarehouseId: event.target.value === 1 ? requirement.pmWarehouseId : null , 
            reqTagId: event.target.value === 2 ? 4 : 3, 
            isRouted: event.target.value === 2 ? 'No' : null 
        })
    }

    const handleOrderType = (event) => {
        setRequirement(prev => ({
            ...prev,
            orderTypeId: event.target.value,
            bussinessTypeId: prev.bussinessTypeId,
            pmWarehouseId: prev.bussinessTypeId === 1 ? prev.pmWarehouseId : null,
            reqTagId: prev.reqTagId,
            isRouted: prev.isRouted
          }))
    }

    const companySearchMethod = (event) => {
        if (event.query === "") {
            setCompanySuggestions([...companies])
        }
        else {
            const searchResults = companyFuzzSearch.search(event.query)
            const relatedCompanies = searchResults.map(eachProduct => eachProduct.item)
            setCompanySuggestions(relatedCompanies)
        }
    }

    const handleCompanyChange = (event) => {
        if (typeof (event.target.value) === "object" && event.target.value !== null) {
            const selectedCompanyId = event.target.value?.companyId
            const isDataPresent = branches.length || gstins.length || users.length || shippingAddresses.length || billingAddresses.length || companyERPCodeMaps.length || rateContractProducts.length || productsWithErpCode.length || productsWithoutErpCode.length
            if (isDataPresent) {
                setBranches([])
                setGstins([])
                setUsers([])
                setShippingAddressesList([])
                setBillingAddressesList([])
                setCompanyERPCodeMaps([])
                setRateContractProducts([])
                setProducts([])
            }

            //ADHOC
            if (requirement.orderTypeId === 1) {
                if (selectedCompanyId === 0) {
                    setVisibleAddNewCompany(true)
                } else {
                    const newRequirement = {
                        ...postRequirementInitialState,
                        orderTypeId: requirement.orderTypeId,
                        companyId: selectedCompanyId,
                        pmWarehouseId: requirement.bussinessTypeId === 1 ? requirement.pmWarehouseId : null,
                        pmBranchId: requirement?.pmBranchId,
                        reqTagId: requirement.reqTagId,
                        isRouted: requirement.isRouted,
                        bussinessTypeId: requirement.bussinessTypeId
                    }
                    setRequirement(newRequirement)

                    let relatedBranches = allCompanies.filter(eachCompany => parseFloat(eachCompany.companyId) === parseFloat(newRequirement?.companyId))
                    relatedBranches = sortArrayOfObjectsInAlphabeticalOrder(relatedBranches, 'branchName')
                    setBranches(relatedBranches)

                    if(newRequirement.bussinessTypeId === 1){
                        getCompanyERPCodeMapData({ companyId: selectedCompanyId, statusId: 1 })
                    }
                }
            }
            //RATECONTRACT
            else if (requirement.orderTypeId === 2) {
                const newRequirement = { 
                    ...postRequirementInitialState, 
                    orderTypeId: requirement.orderTypeId, 
                    companyId: selectedCompanyId, 
                    pmWarehouseId: requirement.bussinessTypeId === 1 ? requirement.pmWarehouseId : null,
                    reqTagId: requirement.reqTagId, 
                    bussinessTypeId: requirement.bussinessTypeId, 
                    isRouted: requirement.isRouted,
                    pmBranchId: requirement.pmBranchId 
                }
                setRequirement(newRequirement)

                let relatedBranches = allCompanies.filter(eachCompany => parseFloat(eachCompany.companyId) === parseFloat(newRequirement.companyId))
                relatedBranches = sortArrayOfObjectsInAlphabeticalOrder(relatedBranches, 'branchName')
                setBranches(relatedBranches)
            }
            //STOCK
            else if (requirement.orderTypeId === 3) {
                const newRequirement = { 
                    ...postRequirementInitialState, 
                    orderTypeId: requirement.orderTypeId, 
                    companyId: selectedCompanyId, 
                    pmWarehouseId: requirement.bussinessTypeId === 1 ? requirement.pmWarehouseId : null,
                    reqTagId: requirement.reqTagId, 
                    bussinessTypeId: requirement.bussinessTypeId, 
                    isRouted: requirement.isRouted,
                    pmBranchId: requirement.pmBranchId 
                }
                setRequirement(newRequirement)

                getBranchData(selectedCompanyId)
                // const relatedBranches = sortArrayOfObjectsInAlphabeticalOrder(pmCompanyDetails.companyBranches, 'branchName')
                // setBranches(relatedBranches)
            }
        }
        else {
            const newRequirement = { 
                ...postRequirementInitialState, 
                orderTypeId: requirement.orderTypeId, 
                companyId: event.target.value, 
                pmWarehouseId: requirement.bussinessTypeId === 1 ? requirement.pmWarehouseId : null,
                reqTagId: requirement.reqTagId, 
                bussinessTypeId: requirement.bussinessTypeId, 
                isRouted: requirement.isRouted,
                pmBranchId: requirement.pmBranchId 
            }
            setRequirement(newRequirement)

            const isDataPresent = branches.length || gstins.length || users.length || shippingAddresses.length || billingAddresses.length || companyERPCodeMaps.length || rateContractProducts.length || productsWithErpCode.length || productsWithoutErpCode.length
            if (isDataPresent) {
                setBranches([])
                setGstins([])
                setUsers([])
                setShippingAddressesList([])
                setBillingAddressesList([])
                setCompanyERPCodeMaps([])
                setRateContractProducts([])
                setProducts([])
            }
        }

        //These were the conditions!
        //typeof(event.target.value) === "string"
        //typeof(event.target.value) === "object" && event.target.value === null
    }

    const handleBranchChange = async (event) => {

        const data = {
            userId: userDetails.userId,
            companyId: requirement.companyId,
            branchId: event.target.value
        }
        setIsLoading(true)
        const response = await getPendingBuyerReq(data)
        setIsLoading(false)
        if(!response.failure){
            let data = response.data
            data = []
            if(data.length > 0){
                setGstins([])
                setUsers([])
                setShippingAddressesList([])
                setBillingAddressesList([])
                setCompanyERPCodeMaps([])
                setRateContractProducts([])
                setProducts([])
                setRequirement({...requirement, branchId: event.target.value})
                setPendingReqList(data)
                setVisiblePendingReqModel(true)
            }else{
                setPendingReqList([])
                setVisiblePendingReqModel(false)
                if (requirement.orderTypeId === 2 && products.length > 0) {
                    setProducts([])
                }
        
                const newRequirement = {
                    ...postRequirementInitialState,
                    orderTypeId: requirement.orderTypeId,
                    companyId: requirement.companyId,
                    branchId: event.target.value,
                    pmWarehouseId: requirement.bussinessTypeId === 1 ? requirement.pmWarehouseId : null,
                    reqTagId: requirement.reqTagId,
                    isRouted: requirement.isRouted,
                    bussinessTypeId: requirement.bussinessTypeId,
                    pmBranchId: requirement.pmBranchId 
                }
        
                if (requirement.orderTypeId === 3) {
                    getCompanyGstinList({ companyIds: [newRequirement.companyId], gstinCodes: [PMCompanyConfig.prefStockGSTINCode], paginator: false, statusId: [1], sortBy: 'gstinCode' })
                } else {
                    // getCompanyGstinList({ companyIds: [newRequirement.companyId], paginator: false, statusId: [1], sortBy: 'gstinCode' })
                    let branchGSTINs = []
                    const relatedBranch = branches.find(eachBranch => eachBranch.branchId === event.target.value)
                    if (relatedBranch && relatedBranch.branchGSTINs) {
                        const relatedBranchGSTINs = typeof (relatedBranch.branchGSTINs) === "string" ? JSON.parse(relatedBranch.branchGSTINs) : relatedBranch.branchGSTINs
                        branchGSTINs = relatedBranchGSTINs?.map(eachGstin => ({
                            gstinId: uuidv4(),
                            gstinCode: eachGstin
                        }))

                        branchGSTINs = sortArrayOfObjectsInAlphabeticalOrder(branchGSTINs, 'gstinCode')
                        setGstins(branchGSTINs)
                    }
                    else {
                        branchGSTINs = [{ gstinId: uuidv4(), gstinCode: 'URP' }] //If there were no gstins for the companyBranches, insert URP as gstins
                        setGstins(branchGSTINs)

                        newRequirement.gstinId = branchGSTINs[0].gstinId

                        getBillingAddressData({ companyIds: [newRequirement.companyId], sortBy: 'billAddress', paginator: false, statusIds: [1] })
                    }
                }
        
                //conditions for getting users and shipping addr
                let userList = []
                if (requirement.orderTypeId === 3) {
                    const data = {
                        companyIds: [newRequirement.companyId],
                        statusId: 1,
                        include: [{ model: "user", attributes: ["firstName", "lastName", "emailId", "phoneNumber"] }],
                        paginator: false
                    }
        
                    userList = await getUserData(data)
                    setActiveTab(1)
                }
                else {
        
                    if (requirement.orderTypeId === 2 && requirement.bussinessTypeId === 1) {
                        const rateContractCompanyDetails = { companyId: newRequirement.companyId, branchIds: [event.target.value, 0], warehouseIds: [requirement.pmWarehouseId] }
                        getBuyerRateContractDetails(rateContractCompanyDetails)
                    }
        
                    const data = {
                        companyIds: [newRequirement.companyId],
                        statusId: 1,
                        include: [{ model: "user", attributes: ["firstName", "lastName", "emailId", "phoneNumber"] }],
                        paginator: false
                    }
        
                    userList = await getUserData(data)
                    getShippingAddressData({ companyIds: [newRequirement.companyId], branchIds: [event.target.value], statusId: 1, paginator: false })
                }
                if(userList.length === 1){
                    newRequirement.userId = userList[0].userId
                }
                setRequirement(newRequirement)
            }
        }
        else{
            return dispatch(raiseToast({ severity: 'error', summary: 'Pending Buyer Reqs', detail: response.data.error, life: 3000 }))
        }
    }

    const handlePMWarehouse = (event) => {
        const updatedRequirement = { ...requirement, pmWarehouseId: event.target.value, shipAddrId: null }

        if(requirement.bussinessTypeId === 2 && requirement.orderTypeId === 1){
            getCompanyERPCodeMapData({ companyId: requirement.companyId, statusId: 1, warehouseIds: [event.target.value]  })
        }

        if (requirement.bussinessTypeId === 2 && requirement.orderTypeId === 2) {
            const rateContractCompanyDetails = { companyId: requirement.companyId, branchIds: [requirement.branchId, 0], warehouseIds: [event.target.value] }
            getBuyerRateContractDetails(rateContractCompanyDetails)
        }

        if (updatedRequirement.orderTypeId === 3) {
            const relatedCompanyWarehouse = pmCompanyDetails.companyWarehouses.find(eachWarehouse => eachWarehouse.warehouseId === event.target.value)
            getShippingAddressData({ shipAddrIds: [relatedCompanyWarehouse.shipAddrId], statusId: 1, paginator: false })
        }

        setRequirement(updatedRequirement)
    }

    const handlePMBranch = (event) => {
        const updatedRequirement = { ...requirement, pmBranchId: event.target.value }
        setRequirement(updatedRequirement)
    }

    const handleGstinChange = async (event) => {
        requirement.gstinId = event.target.value
        const reqGstinCode = gstins.find(eachGstin => eachGstin.gstinId === event.target.value)

        let data = {}
        if (reqGstinCode?.gstinCode === "URP") {
            data = {
                companyIds: [requirement.companyId],
                gstinCodes: [reqGstinCode.gstinCode],
                sortBy: 'billAddress',
                sortOrder: 'ASC',
                paginator: false,
                statusIds: [1]
            }
        } else {
            data = {
                companyIds: [requirement.companyId],
                gstinCodes: [reqGstinCode.gstinCode],
                sortBy: 'billAddress',
                sortOrder: 'ASC',
                paginator: false,
                statusIds: [1]
            }
        }
        getBillingAddressData(data)
        setRequirement({ ...requirement, billAddrId: null })
    }

    const handleUserChange = (event) => {
        requirement.userId = event.target.value
        setRequirement({ ...requirement })
    }

    const handleRoutedChange = (event) => {
        requirement.isRouted = event.target.value
        setRequirement({ ...requirement })
    }

    const handleReqTagChange = (e) => {
        setRequirement({ ...requirement, reqTagId: e.target.value })
    }

    const handleShippingAddress = (id) => {
        requirement.shipAddrId = id
        setRequirement({ ...requirement })
    }

    const handleBillingAddress = (id) => {
        requirement.billAddrId = id
        setRequirement({ ...requirement })
    }

    const handleRequirementCreatedTime = (e) => {
        setRequirement({ ...requirement, requirementDate: e.value })
    }

    const handleReqSource = (e) => {
        requirement.reqSource = e.target.value
        if (e.target.value !== "Mail"){
            requirement.ticketId = null
        }
        setRequirement({ ...requirement })
    }

    const handleTaxExemption = (e) => {
        const newValue = e.target.value
        if (requirement.orderTypeId === 2){
            const isNonTaxExemptedItemAdded = products.some(eachProduct => eachProduct.isTaxExempted === false)
            const isTaxExemptedItemAdded = products.some(eachProduct => eachProduct.isTaxExempted)

            if (newValue === "Yes" && isNonTaxExemptedItemAdded){
                return dispatch(raiseToast({ severity: 'warn', detail: "Cannot change as non-tax exempted items already added.", life: 3000 }))
            }else if (newValue === "No" && isTaxExemptedItemAdded){
                return dispatch(raiseToast({ severity: 'warn', detail: "Cannot change as tax exempted items already added.", life: 3000 }))
            }
        }
        setRequirement(prev => ({ ...prev, isTaxExempted: newValue }))
    }

    const removeSourceDocument = () => {
        requirement.reqSourceDoc = null
        setRequirement({ ...requirement })
    }

    const handleAutomateRC = (event) => {
        const updatedRequirement = {...requirement, isRCAutomated: event.target.checked}
        setRequirement(updatedRequirement)
    }

    const handleAutomateRCOnHide = () => {
        const updatedRequirement = {...requirement, isRCAutomated: false}
        setRequirement(updatedRequirement)
        setVisibleAutomateRCModal(false)
    }

    const handleAdhocEmailRecipientDetailsOnHide = () => {
        const updatedRequirement = { ...requirement, emailSubject: null, emailRecipient: null, emailDateStamp: null }
        setRequirement(updatedRequirement)
        setVisibleAdhocEmailRecipientDetails(false)
    }

    const renderAutomateRCFooter = () => {
        return (
            <div className="mt-2">
                {/* <Button label="Cancel" severity="danger" outlined onClick={handleAutomateRCOnHide} /> */}
                <Button label="Proceed" outlined onClick={handlePostRequirementSubmit} disabled={isLoading} />
            </div>
        )
    }

    const renderAdhocEmailRecipientDetails = () => {
        return (
            <div className="mt-2">
                <Button label="Proceed" outlined onClick={handlePostRequirementSubmit} disabled={isLoading} />
            </div>
        )
    }

    const renderIncrementalReqFooter = () => {
        return (
            <div className="mt-2">
                <Button label="Proceed" outlined onClick={handleAutomateRCSubmit} disabled={isLoading} />
            </div>
        )
    }

    // All event handlers for products
    const handleAddProductWithErpCode = () => {
        const newProduct = {
            productId: uuidv4(),
            companyERPCode: '',
            materialId: '',
            productName: '',
            variantName: '',
            specification: '',
            categoryId: null,
            brandId: null,
            uomValue: null,
            materialUOMId: null,
            quantity: '',
            isMappedProduct: true,
            buyerRateContractId: null,
            companyERPCodeMapId: null,
            isOverRiddedProduct: false,
            isMaterialMasterProduct: false
        }

        const newProductsList = [...products, newProduct]
        setProducts(newProductsList)
    }

    const handleAddProductWithoutErpCode = () => {
        const newProduct = {
            productId: uuidv4(),
            companyERPCode: '',
            materialId: '',
            productName: '',
            // variantName: '',
            specification: '',
            category: null,
            brand: null,
            uomValue: null,
            materialUOMId: null,
            quantity: '',
            isMappedProduct: false,
            buyerRateContractId: null,
            companyERPCodeMapId: null, //Always null
            isOverRiddedProduct: false,
            isMaterialMasterProduct: false
        }

        const newProductsList = [...products, newProduct]
        setProducts(newProductsList)
    }

    const handleRemoveProduct = (id) => {
        const newProductsList = products.filter(eachProduct => eachProduct.productId !== id)
        setProducts([...newProductsList])
    }

    const handleProductChange = (id, name, value) => {
        const updatedProductsList = products.map(eachProduct => {
            if (eachProduct.productId === id) {
                return { ...eachProduct, [name]: value }
            }
            return eachProduct
        })
        setProducts([...updatedProductsList])
    }

    const handleClientReqNoChange = (e) => {
        let updatedRequirement = {...requirement, clientReqNo: e.target.value}
        setRequirement(updatedRequirement)
    }

    const handleAutoFillProductData = (id, productData) => {
        const updatedProducts = products.map(eachProduct => {
            if (eachProduct.productId === id) {
                return { productId: id, ...productData }
            }
            return eachProduct
        })
        setProducts([...updatedProducts])
    }

    const getUpdatedProductWithMaterialCode = async (id, companyERPCode, materialId) => {

        const materialDetails = await getMaterialMasterAndTempMaterialIdsById({ materialId: materialId, excludeTempMaterialsUnderDiffClientReq: true, companyId: requirement.companyId })
        if (materialDetails.failure) {
            return { error: "raise error", errorMsg: materialDetails.data.error }
        }
        else {
            const material = materialDetails?.data

            if (!material) dispatch(raiseToast({ severity: 'error', detail: "Material ID not found", life: 3000 }))

            const materialUOMDetails = uomsData.find(eachUOM => eachUOM.UOM === material?.UOM)
            let materialUOMValue = material?.UOMValue
            if (!material?.UOMValue && material?.materialId.toString().length >= 7) {
                materialUOMValue = 1
            }

            const updatedProducts = products.map(eachProduct => {
                if (eachProduct.productId === id) {
                    let updatedObj = {
                        // ...eachProduct, 
                        productId: id,
                        companyERPCode: eachProduct.companyERPCode,
                        materialId: material?.materialId ? material?.materialId : '',
                        productName: material?.productName ? `${material?.productName}${material?.variantName ? `-${material?.variantName}` : ''}` : '',
                        materialMasterProductName: material?.productName ? material?.productName : null,
                        specification: material?.productSpecification ? material?.productSpecification : '',
                        materialMasterSpecification: material?.productSpecification ? material?.productSpecification : null,
                        // brand: material?.brandId ,
                        brand: material?.brandId ? material?.brandId : null,
                        category: material?.categoryId ? material?.categoryId : null,
                        materialUOMId: materialUOMDetails ? materialUOMDetails.materialUOMId : null,
                        uomValue: materialUOMValue ? materialUOMValue : '',
                        isMappedProduct: false,
                        isMaterialMasterProduct: material ? true : false,
                        buyerRateContractId: null,
                        companyERPCodeMapId: null,
                        isOverRiddedProduct: false
                    }

                    if(requirement.orderTypeId === 3){
                        updatedObj.companyERPCode = material?.materialId ? material?.materialId : ""
                    }
                    return updatedObj
                }
                return eachProduct
            })
            return updatedProducts
        }
    }

    const getEmptiedProducts = (id, companyERPCode, materialId) => {
        const updatedProducts = products.map(eachProduct => {
            if (eachProduct.productId === id) {
                return {
                    // ...eachProduct,
                    productId: id,
                    companyERPCode: companyERPCode,
                    materialId: materialId,
                    productName: '',
                    // variantName: '',
                    specification: '',
                    category: null,
                    brand: null,
                    uomValue: null,
                    materialUOMId: null,
                    quantity: '',
                    isMappedProduct: false,
                    buyerRateContractId: null,
                    companyERPCodeMapId: null,
                    isOverRiddedProduct: false,
                    isMaterialMasterProduct: false
                }
            }
            return eachProduct
        })

        return updatedProducts
    }

    const handleMoveToSectiontwo = async (id, companyERPCode, materialId) => {

        //We need to call material Id api for only orderType = 1, 3

        let updatedProducts = []
        if (requirement.orderTypeId === 2) return

        if (requirement.orderTypeId === 1) {
            if (materialId) {
                setIsLoading(true)
                updatedProducts = await getUpdatedProductWithMaterialCode(id, companyERPCode, materialId)
                setIsLoading(false)

                if (updatedProducts.error === "raise error") return dispatch(raiseToast({ severity: 'error', summary: 'Material Codes', detail: updatedProducts.errorMsg, life: 3000 }))
            }
            else if (companyERPCode) {
                updatedProducts = getEmptiedProducts(id, companyERPCode, materialId)
                dispatch(raiseToast({ severity: 'warn', detail: 'Company ERP code is not mapped. Product moved to unmapped products section', life: 3000 }))
            }
        }

        setActiveTab(1)
        setProducts([...updatedProducts])
    }

    const handleUnmappedProductsMoves = async (id, companyERPCode, materialId) => {

        let updatedProducts = []
        if ([1, 3].includes(requirement.orderTypeId)){
            setIsLoading(true)
            updatedProducts = await getUpdatedProductWithMaterialCode(id, companyERPCode, materialId)
            if (updatedProducts.error === "raise error"){ 
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', summary: 'Material Codes', detail: updatedProducts.errorMsg, life: 3000 }))
            }
            setIsLoading(false)
        }

        setActiveTab(1)
        setProducts([...updatedProducts])
    }

    const handleActiveTab = (num) => {
        setActiveTab(num)
    }

    const handleRemoveAllSec1Products = (products) => {
        const isProductsWithERP = products.find(eachProduct => eachProduct.isMappedProduct)
        if (isProductsWithERP) {
            if (window.confirm("Do you want to remove all the products in mapped products section?")) {
                const filteredProductsData = products.filter(eachProduct => !eachProduct.isMappedProduct)
                setProducts(filteredProductsData)
            }
        }
    }

    const handleRemoveAllSec2Products = (products) => {
        const isProductsWithERP = products.find(eachProduct => !eachProduct.isMappedProduct)
        if (isProductsWithERP) {
            if (window.confirm("Do you want to remove all the products in unmapped products section?")) {
                const filteredProductsData = products.filter(eachProduct => eachProduct.isMappedProduct)
                setProducts(filteredProductsData)
            }
        }
    }

    //Sheet handlers
    const uploadADHOCSheet = [
        {
            label: 'Download ADHOC Sample',
            command: () => {
                const sampleProductData = [
                    {
                        "S.No": 1,
                        "Company ERP Code": "ERP07010",
                        "Material ID": 102934,
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                    {
                        "S.No": 2,
                        "Company ERP Code": '',
                        "Material ID": 102932,
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                    {
                        "S.No": 3,
                        "Company ERP Code": "ERP07017",
                        "Material ID": '',
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                    {
                        "S.No": 4,
                        "Company ERP Code": 'ERP87893',
                        "Material ID": 700001,
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                    {
                        "S.No": 5,
                        "Company ERP Code": '',
                        "Material ID": 700003,
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                    {
                        "S.No": 6,
                        "Company ERP Code": '',
                        "Material ID": 102843,
                        "Product": "Book",
                        "Specification": "dsvvdsfadf",
                        "Category": "Housekeeping",
                        "Brand": "puma",
                        "UOM Value": 1,
                        "UOM": "NOS",
                        "Quantity": 20
                    },
                ]

                const worksheet = utils.json_to_sheet(sampleProductData)
                const workbook = utils.book_new()
                utils.book_append_sheet(workbook, worksheet, "Data")
                writeFile(workbook, "sampleADHOCSheet.xlsx")
            }
        },
        {
            label: 'Upload File',
            command: () => {
                const fileInput = document.getElementById('upload-xl-form');
                if (fileInput) {
                    fileInput.click()
                }
            }
        }
    ]

    const uploadRCSheet = [
        {
            label: 'Download RC Sample',
            command: () => {
                const rateContractProductsSheet = rateContractProducts.map((eachProduct, index) => ({
                    "S.No": index + 1,
                    "Rate contract ID": eachProduct.buyerRateContractId,
                    "Product-Variant": `${eachProduct.companyProdName}${eachProduct.variantName ? `-${eachProduct.variantName}` : ''}`,
                    "Specification": eachProduct.companyProdSpec,
                    "Category": eachProduct.categoryName,
                    "Brand": eachProduct.brandName,
                    "UOM value-UOM": `${eachProduct.UOMValue}${eachProduct.UOM ? `-${eachProduct.UOM}` : ''}`,
                    "Quantity": ''
                }))

                const sampleProductData = rateContractProducts.length ? rateContractProductsSheet : [{ "S.No": "", "Rate contract ID": '', "Product-Variant": "", "Specification": "", "Category": '', "Brand": "", "UOM value-UOM": "", "Quantity": '', }]
                const worksheet = utils.json_to_sheet(sampleProductData)
                const workbook = utils.book_new()
                utils.book_append_sheet(workbook, worksheet, "Data")
                writeFile(workbook, "sampleRCSheet.xlsx")
            }
        },
        {
            label: 'Upload File',
            command: () => {
                const fileInput = document.getElementById('upload-xl-form');
                if (fileInput) {
                    fileInput.click()
                }
            }
        }
    ]

    const handlProductsUploadFile = async (e) => {
        setIsLoading(true)

        const { error, errorMsg } = await checkFileType({file: e.target.files[0], allowedFileTypes: ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']})
        if (error) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: "error", detail: errorMsg, life: 3000 }))
        }

        const reader = new FileReader()
        if (e.target.files[0]) {
            reader.readAsArrayBuffer(e.target.files[0])
            reader.onload = async (e) => {
                const data = e.target.result

                const workbook = read(data)
                const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                const productsParsedData = utils.sheet_to_json(worksheet)

                if (requirement.orderTypeId === 1) {
                    const allHeaders = new Set()
                    productsParsedData.forEach(item => {
                        Object.keys(item).forEach(key => {
                            allHeaders.add(key)
                        })
                    })
                    const headers = Array.from(allHeaders);

                    const requiredColumns = ["S.No", "Company ERP Code", "Material ID", "Product", "Specification", "UOM Value", "Category", "Brand", "UOM", "Quantity"]
                    requiredColumns.forEach(eachColumnName => {
                        if (headers.includes(eachColumnName) === false) {
                            return dispatch(raiseToast({ severity: "error", summary: "Invalid Sheet", detail: `Sheet missing ${eachColumnName} for some products`, life: 3000 }))
                        }
                    })

                    let materialIDCounter = 1
                    const invalidMaterialCode = productsParsedData.some(eachProduct => {
                        if (!(/^$|^-?\d+$/.test(eachProduct["Material ID"])) && eachProduct["Material ID"]) {
                            materialIDCounter += 1
                            return true
                        }
                        return false
                    })

                    if (invalidMaterialCode) return dispatch(raiseToast({ severity: 'error', summary: 'Quantity', detail: `Please give proper Material Code at line ${materialIDCounter}`, life: 3000 }))

                    //Improper qty & empty qty validation
                    let counter = 1 
                    let filteredSheetData = []
                    const invalidProductsPresent = productsParsedData.some(eachProduct => {
                        if (eachProduct["Quantity"] && !(/^$|^-?(\d+(\.\d+)?|\.\d+)$/.test(eachProduct["Quantity"]))) {
                            counter += 1
                            return true
                        }

                        if (parseFloat(eachProduct?.["Quantity"]) > 0) {
                            eachProduct["Quantity"] = round(eachProduct["Quantity"], 3)
                            filteredSheetData.push(eachProduct)
                        }
                        return false
                    })
                    if (invalidProductsPresent) return dispatch(raiseToast({ severity: 'error', summary: 'Quantity', detail: `Please give proper quantity at line ${counter}`, life: 3000 }))

                    let sNo =0 
                    const invalidSpecs = productsParsedData.some(eachProduct => {
                        if (eachProduct["Specification"].length < 3) {
                            sNo += 1
                            return true
                        }
                        return false
                    })
                    if (invalidSpecs){ 
                        return dispatch(raiseToast({ severity: 'error',  detail: `Product Specifications should be at least 3 characters, Please refer line no ${sNo}`, life: 3000 }))
                    }

                    const constructProduct = (product) => {
                        let relatedBrand = null
                        let relatedCategory = null
                        let relatedUOM = null
                        if (product?.brandName) {
                            relatedBrand = brandsData.find(eachBrand => eachBrand.brandName === product.brandName)
                        }
                        if (product?.categoryName) {
                            relatedCategory = industryData.find(eachCategory => eachCategory.categoryName === product.categoryName)
                        }
                        if (product?.UOM) {
                            relatedUOM = uomsData.find(eachUOM => eachUOM.UOM === product.UOM)
                        }

                        return {
                            productId: uuidv4(),
                            companyERPCode: product?.companyERPCode ? product?.companyERPCode : null,
                            materialId: product?.materialId ? product?.materialId : null,
                            productName: product?.productName,
                            specification: product?.specification,
                            brand: relatedBrand ? relatedBrand.brandId : null,
                            category: relatedCategory ? relatedCategory.categoryId : null,
                            quantity: product.qty,
                            materialUOMId: relatedUOM ? relatedUOM.materialUOMId : null,
                            uomValue: product.uomValue,
                            isMappedProduct: false,
                            buyerRateContractId: null,
                            isOverRiddedProduct: true,
                            isERPCodeFound: product?.isERPCodeFound ? true : false,
                            isMaterialIDFound: product?.isMaterialIDFound ? true : false,
                            isValidMaterialId: product?.isValidMaterialId ? true : false
                        }
                    }

                    let materialCheckProducts = []
                    let uploadedProducts = _.compact(filteredSheetData.map(eachProduct => {
                        const companyERPCodeMappedProduct = companyERPCodeMaps.find(eachErpMap => {
                            if (eachErpMap.companyERPCode === eachProduct["Company ERP Code"] && eachProduct["Company ERP Code"]) return true
                            else if (eachErpMap.materialId === eachProduct?.["Material ID"] && eachProduct?.["Material ID"]) return true
                            return false
                        })

                        if (companyERPCodeMappedProduct) {
                            return {
                                productId: uuidv4(),
                                materialId: companyERPCodeMappedProduct.materialId,
                                companyERPCode: companyERPCodeMappedProduct.companyERPCode,
                                productName: companyERPCodeMappedProduct.companyProdName,
                                variantName: companyERPCodeMappedProduct.variantName,
                                specification: companyERPCodeMappedProduct.companyProdSpec,
                                brandId: companyERPCodeMappedProduct.brandId,
                                categoryId: companyERPCodeMappedProduct.categoryId,
                                quantity: eachProduct["Quantity"],
                                materialUOMId: companyERPCodeMappedProduct.materialUOMId,
                                companyERPCodeMapId: companyERPCodeMappedProduct.companyERPCodeMapId,
                                uomValue: companyERPCodeMappedProduct.UOMValue,
                                isMappedProduct: true,
                                buyerRateContractId: null,
                                isOverRiddedProduct: false
                            }
                        } 
                        else if(requirement.bussinessTypeId === 1) {

                            //Firts priority -> ERP Code
                            //Second priority -> Material ID
                            //CHeck material Id is valid or not!

                            if (eachProduct?.["Company ERP Code"]) {
                                return constructProduct({
                                    companyERPCode: eachProduct["Company ERP Code"],
                                    materialId: null,
                                    productName: eachProduct["Product"],
                                    specification: eachProduct["Specification"],
                                    brandName: eachProduct["Brand"],
                                    categoryName: eachProduct["Category"],
                                    qty: eachProduct["Quantity"],
                                    UOM: eachProduct["UOM"],
                                    uomValue: eachProduct["UOM Value"],
                                    isERPCodeFound: true,
                                    isMaterialIDFound: eachProduct?.["Material ID"] ? true : false
                                })
                            }

                            else if (eachProduct?.["Material ID"]) {
                                materialCheckProducts.push({
                                    companyERPCode: null,
                                    materialId: eachProduct["Material ID"],
                                    productName: eachProduct["Product"],
                                    specification: eachProduct["Specification"],
                                    brandName: eachProduct["Brand"],
                                    categoryName: eachProduct["Category"],
                                    qty: eachProduct["Quantity"],
                                    UOM: eachProduct["UOM"],
                                    uomValue: eachProduct["UOM Value"],
                                    isERPCodeFound: eachProduct["Company ERP Code"] ? true : false,
                                    isMaterialIDFound: true
                                })

                                return null
                            }

                            else {
                                return constructProduct({
                                    companyERPCode: null,
                                    materialId: null,
                                    productName: eachProduct["Product"],
                                    specification: eachProduct["Specification"],
                                    brandName: eachProduct["Brand"],
                                    categoryName: eachProduct["Category"],
                                    qty: eachProduct["Quantity"],
                                    UOM: eachProduct["UOM"],
                                    uomValue: eachProduct["UOM Value"],
                                    isERPCodeFound: false,
                                    isMaterialIDFound: false
                                })
                            }
                        }
                    }))

                    if (materialCheckProducts?.length > 0) {
                        const materialIds = materialCheckProducts.map(eachMaterial => eachMaterial.materialId)
                        const materialProducts = await getMaterialMasterAndTempMaterialIds({ materialIds: materialIds })

                        if (materialProducts.failure) {
                            setIsLoading(false)
                            return dispatch(raiseToast({ severity: 'error', summary: 'material Details', detail: materialProducts.data.error, life: 3000 }))
                        } else {
                            materialCheckProducts.forEach(eachProduct => {
                                let isFound = false
                                materialProducts?.data?.forEach(eachMaterial => { //can be optimised. Use find!
                                    if (eachProduct.materialId === eachMaterial.materialId) {
                                        isFound = true
                                        const relatedUOM = uomsData.find(eachUOM => eachUOM.UOM === eachMaterial.UOM)

                                        uploadedProducts.push({
                                            productId: uuidv4(),
                                            companyERPCode: null,
                                            materialId: eachMaterial.materialId,
                                            productName: eachMaterial?.productName,
                                            specification: eachMaterial?.productSpecification,
                                            brand: eachMaterial?.brandId,
                                            category: eachMaterial?.categoryId,
                                            quantity: eachProduct.qty,
                                            materialUOMId: relatedUOM ? relatedUOM.materialUOMId : null,
                                            uomValue: eachProduct.uomValue,
                                            isMappedProduct: false,
                                            buyerRateContractId: null,
                                            isOverRiddedProduct: true,
                                            isERPCodeFound: eachProduct["Company ERP Code"] ? true : false,
                                            isMaterialIDFound: true,
                                            isValidMaterialId: true
                                        })
                                    }
                                })
                                if (!isFound) {
                                    const newProduct = constructProduct({
                                        companyERPCode: null,
                                        materialId: null,
                                        productName: eachProduct?.productName,
                                        specification: eachProduct?.specification,
                                        brandName: eachProduct?.brandName,
                                        categoryName: eachProduct?.categoryName,
                                        qty: eachProduct?.qty,
                                        UOM: eachProduct?.UOM,
                                        uomValue: eachProduct?.uomValue,
                                        isOverRiddedProduct: true,
                                        isERPCodeFound: eachProduct["Company ERP Code"] ? true : false,
                                        isMaterialIDFound: true,
                                        isValidMaterialId: false
                                    })
                                    uploadedProducts.push(newProduct)
                                }
                            })

                        }
                    }

                    setProducts([...uploadedProducts])
                }
                if (requirement.orderTypeId === 2) {

                    //Improper qty & empty qty validation
                    let counter = 1 //First line in the xl sheet is heading
                    let filteredSheetData = []
                    const invalidProductsPresent = productsParsedData.some(eachProduct => {
                        if (!(/^$|^-?(\d+(\.\d+)?|\.\d+)$/.test(eachProduct["Quantity"])) && eachProduct["Quantity"]) {
                            counter += 1
                            return true
                        }

                        if (parseFloat(eachProduct["Quantity"]) > 0) {
                            eachProduct["Quantity"] = round(eachProduct["Quantity"], 3)
                            filteredSheetData.push(eachProduct)
                        }
                        return false
                    })
                    if (invalidProductsPresent) return dispatch(raiseToast({ severity: 'error', summary: 'Quantity', detail: `Please give proper quantity at ${counter} line`, life: 3000 }))
                    let sNo = 0
                    const invalidSpecs = productsParsedData.some(eachProduct => {
                        if (eachProduct["Specification"].length < 3) {
                            sNo += 1
                            return true
                        }
                        return false
                    })
                    if (invalidSpecs) {
                        return dispatch(raiseToast({ severity: 'error', detail: `Product Specifications should be at least 3 characters, Please refer line no ${sNo}`, life: 3000 }))
                    }

                    let isWrongRateContractProducts = false
                    const uploadedProducts = _.compact(filteredSheetData.map(eachProduct => {
                        const relatedRCProduct = rateContractProducts.find(eachRCProduct => eachRCProduct.buyerRateContractId === eachProduct["Rate contract ID"])
                        if (!relatedRCProduct) {
                            isWrongRateContractProducts = true
                            return
                        }

                        if (relatedRCProduct?.companyERPCode) {
                            return {
                                productId: uuidv4(),
                                materialId: relatedRCProduct?.materialId,
                                companyERPCode: relatedRCProduct?.companyERPCode,
                                productName: relatedRCProduct?.companyProdName,
                                variantName: relatedRCProduct?.variantName,
                                specification: relatedRCProduct?.companyProdSpec,
                                brandId: relatedRCProduct?.brandId,
                                categoryId: relatedRCProduct?.categoryId,
                                quantity: eachProduct["Quantity"],
                                materialUOMId: relatedRCProduct?.materialUOMId,
                                uomValue: relatedRCProduct?.UOMValue,
                                isMappedProduct: true,
                                buyerRateContractId: relatedRCProduct?.buyerRateContractId,
                                companyERPCodeMapId: relatedRCProduct?.companyERPCodeMapId ? relatedRCProduct?.companyERPCodeMapId : null,
                                isOverRiddedProduct: false
                            }
                        }
                        else {
                            return {
                                productId: uuidv4(),
                                companyERPCode: null,
                                materialId: relatedRCProduct?.materialId,
                                productName: relatedRCProduct?.companyProdName,
                                specification: relatedRCProduct?.companyProdSpec,
                                brand: relatedRCProduct?.brandId,
                                category: relatedRCProduct?.categoryId,
                                quantity: eachProduct["Quantity"],
                                materialUOMId: relatedRCProduct?.materialUOMId,
                                uomValue: relatedRCProduct?.UOMValue,
                                isMappedProduct: false,
                                buyerRateContractId: relatedRCProduct?.buyerRateContractId,
                                companyERPCodeMapId: relatedRCProduct?.companyERPCodeMapId ? relatedRCProduct?.companyERPCodeMapId : null,
                                isOverRiddedProduct: false
                            }
                        }
                    }))

                    if (isWrongRateContractProducts) dispatch(raiseToast({ severity: "error", summary: 'Invalid rate contact products', detail: `Please check every product is a rate contract product`, life: 3000 }))
                    setProducts([...uploadedProducts])
                }
            }
        }
        e.target.value = ""
        setIsLoading(false)
    }

    const handleSourceDocUpload = async ({ files, options }) => {
        const {fileArr, error, errorMsg} = await getUploadedFileObj({ files, options, documentName: "REQUIREMENT_SOURCE_DOCUMENT" })
        if(error){
            return dispatch(raiseToast({ severity: 'error', detail: errorMsg, life: 3000 }))
        }
        setRequirement({ ...requirement, reqSourceDoc: fileArr[0] })
    }

    const handleRequirementChange = async (e) => {
        let updatedRequirement = {...requirement, [e.target.name]: e.target.value}
        setRequirement(updatedRequirement)
    }

    //submit handlers
    const handleAddNewCompanySubmit = async (newCompanyData, urpCheck) => {
        if (newCompanyData) {
            const activeRole = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)
            newCompanyData.prefPMBranchId = activeRole?.branchId

            if (window.confirm('Are sure you want to add new company?')) {

                let response = null;
                if (urpCheck) {
                    const panCodeCheck = newCompanyData.panCode ? newCompanyData.panCode : undefined
                    response = await addNewCompanyFromBuyerReq({ ...newCompanyData, panCode: panCodeCheck, gstinCode: null, statusId: 5, companyRoles: [{ roleName: "HOD" }, { roleName: "REGIONAL HOD" }, { roleName: "BRANCH HEAD" }] })
                }
                else {
                    response = await addNewCompanyFromBuyerReq({ ...newCompanyData, statusId: 5, companyRoles: [{ roleName: "HOD" }, { roleName: "REGIONAL HOD" }, { roleName: "BRANCH HEAD" }] })
                }

                if (response.failure) {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'error', summary: 'Add new company', detail: response.data.error, life: 3000 }))
                }
                else {

                    const newCompanyDetails = response.data.companyResponse.newlyAddedData.newCompanyDetails
                    setCompanies([...companies, newCompanyDetails])

                    const newBranchDetails = response.data.companyResponse.newlyAddedData.newBranchDetails
                    setBranches([newBranchDetails])

                    const updatedAllCompanyDetails = { ...response.data.companyResponse.newlyAddedData.newCompanyDetails, ...newBranchDetails }
                    setAllCompanies([updatedAllCompanyDetails])

                    let newGstin = []
                    if (urpCheck) {
                        newGstin = { gstinId: uuidv4(), gstinCode: "URP" } // If urp we are not creating any row in companyGSTIN
                        setGstins([newGstin])
                    } else {
                        newGstin = response.data.companyResponse.newlyAddedData.newGstinDetails
                        setGstins([{ gstinId: newGstin?.gstinId, gstinCode: newGstin?.gstinCode }])
                    }

                    const userRes = response.data.companyResponse.newlyAddedData.newUserDetails
                    let newUser = {
                        userId: userRes.userId,
                        fullName: userRes.firstName + " " + (userRes.lastName ? userRes.lastName : '')
                    }
                    setUsers([newUser])

                    const newShippAddr = {
                        shipAddrId: response.data.companyResponse.newlyAddedData.newShippingAddrr.shipAddrId,
                        shipAddr: response.data.companyResponse.newlyAddedData.newShippingAddrr.shipAddr,
                        city: response.data.companyResponse.newlyAddedData.newShippingAddrr.city,
                        stateId: response.data.companyResponse.newlyAddedData.newShippingAddrr.stateId,
                        // isSelect: true
                    }
                    setShippingAddressesList([newShippAddr])

                    let newBillAddr = {
                        billAddrId: response.data.companyResponse.newlyAddedData.newBillAddrr.billAddrId,
                        billAddress: response.data.companyResponse.newlyAddedData.newBillAddrr.billAddress,
                        loc: response.data.companyResponse.newlyAddedData.newBillAddrr?.loc,
                        gstinCode: response.data.companyResponse.newlyAddedData.newBillAddrr?.gstinCode,
                        stateId: response.data.companyResponse.newlyAddedData.newBillAddrr?.stateId
                        // isSelect: true 
                    }
                    setBillingAddressesList([newBillAddr])
                    const newRequirement = {
                        ...requirement,
                        companyId: newCompanyDetails.companyId,
                        branchId: newBranchDetails.branchId,
                        gstinId: newGstin?.gstinId,
                        userId: newUser.userId,
                        shipAddrId: newShippAddr.shipAddrId,
                        billAddrId: newBillAddr.billAddrId
                    }
                    setRequirement(newRequirement)

                    setVisibleAddNewCompany(false)
                    setIsLoading(false)
                }
            }
            else {
                setIsLoading(false)
            }
        }
    }

    const handleAddShippingAddressSubmit = async (formResult) => {
        if (window.confirm("Do you want to add shipping address?")) {
            const { companyId, branchId } = requirement
            const { address, city, pinCode, stateId, countryId } = formResult

            if (formResult) {
                setIsLoading(true)
                const data = { companyId, branchId, city, pinCode, stateId, shipAddr: address, countryId }
                const response = await addCompanyBranchShipAddrData({ ...data })

                if (response.failure) return dispatch(raiseToast({ severity: 'error', summary: 'Add Shipping Address', detail: response.data.error, life: 3000 }))

                const newShiipingAddress = {
                    shipAddrId: response.data.shipAddrId,
                    shipAddr: response.data.shipAddr,
                    city: response.data.city,
                    stateId: response.data.stateId,
                    countryId: response.data.countryId
                }

                const newShippAddrList = [...shippingAddresses, newShiipingAddress]

                setRequirement({ ...requirement, shipAddrId: newShiipingAddress.shipAddrId })
                setShippingAddressesList(newShippAddrList)
                setVisibleAddNewShippingAddr(false)

                setIsLoading(false)
            }
        }
    }

    const handleIncrementalReqSubmit = () => {
        const allProducts = [...productsWithErpCode, ...productsWithoutErpCode]

        let updatedProducts = []
        allProducts.map(eachProduct => {
            if (eachProduct.materialId){
                const relatedIncrementalReqLog = incrementalReqLogs.find(eachIncrementalReqLog => eachIncrementalReqLog.materialId === eachProduct.materialId)
                
                eachProduct.brandId = eachProduct.brand
                eachProduct.categoryId = eachProduct.category
                eachProduct.productSpecification = eachProduct.specification

                if (relatedIncrementalReqLog){
                    let increasedQty = parseFloat(eachProduct.quantity) - parseFloat(relatedIncrementalReqLog.qty)
                    if (increasedQty > 0){
                        updatedProducts.push({...eachProduct, qty: increasedQty})
                    }
                }else {
                    let qty = parseFloat(eachProduct.quantity)
                    updatedProducts.push({...eachProduct, qty: qty})
                }
            }else{
                eachProduct.brandId = eachProduct.brand.brandId
                eachProduct.categoryId = eachProduct.category
                eachProduct.productSpecification = eachProduct.specification
                eachProduct.qty = eachProduct.quantity

                updatedProducts.push(eachProduct)
            }
        })

        updatedProducts = updatedProducts.filter(eachProduct => eachProduct.qty > 0)

        if (!updatedProducts.length){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please increase the Qty for existing ones or add new materials for req creation.'}))
        }

        setIncrementalReqProducts(updatedProducts)
        setIsIncrementalReqConfirmModalVisible(true)
    }

    const handleAutomateRCSubmit = () => {
        if (requirement.orderTypeId === 2) {
            setVisibleAutomateRCModal(true)
        } 
        else if (!requirement.ticketId && requirement.orderTypeId === 1 && requirement.reqSource === "Mail") {
            setVisibleAdhocEmailRecipientDetails(true)
        }
        else {
            handlePostRequirementSubmit()
        }
    }

    const handlePostRequirementValidations = async () => {
        const relatedCompany = companies.find(eachCompany => eachCompany.companyId === requirement.companyId)
        const updatedRequirement = {...requirement, isIncrementalReq: requirement.buyerReqId ? true : false}
        const validationRes = await validateInputs(PostRequirementSchem, updatedRequirement)

        let unknownCategory = {
            categoryId: 22,
            categoryName: "Unknown",
            createdAt: "2023-08-11T10:02:08.609Z",
            createdBy: 1,
            industryName: "Procmart",
            statusId: 2,
            updatedAt: "2023-08-11T10:02:08.609Z",
            updatedBy: 1
        }

        let updatedIndustryData = [...industryData, unknownCategory]
        if (validationRes.error) {

            if (validationRes.error.isRouted) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.isRouted, life: 3000 }))
            }
            else if (validationRes.error.reqTagId) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.reqTagId, life: 3000 }))
            }
            else if (validationRes.error.shipAddrId) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.shipAddrId, life: 3000 }))
            }
            else if (validationRes.error.billAddrId) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.billAddrId, life: 3000 }))
            }
            else if (validationRes.error.requirementDate) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.requirementDate, life: 3000 }))
            }
            else if (validationRes.error.reqSource) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.reqSource, life: 3000 }))
            }
            else if (validationRes.error.reqSourceDoc) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.reqSourceDoc, life: 3000 }))
            }
            else if (validationRes.error.clientReqNo) {
                dispatch(raiseToast({ severity: 'error', detail: validationRes.error.clientReqNo, life: 3000 }))
            }
            setFormErrors(validationRes.error)
        }
        else {

            if (!requirement?.reqSourceDoc) {
                return dispatch(raiseToast({ severity: 'error', detail: "Source document is missing.", life: 3000 }))
            }

            if (relatedCompany.tags?.hasRCTaxExemption){
                if (!requirement.isTaxExempted){
                    return dispatch(raiseToast({ severity: 'error', detail: "Please select tax exemption." }))
                }
            }

            setFormErrors({})

            //Products validations
            if (productsWithErpCode.length === 0 && productsWithoutErpCode.length === 0) {
                return dispatch(raiseToast({ severity: 'error', detail: "Please Add Products", life: 3000 }))
            }

            const invalidMappedProduct = productsWithErpCode.some((eachProduct, index) => {
                if (!eachProduct.quantity || parseFloat(eachProduct.quantity) === 0) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add quantity for material code ${eachProduct.materialId} in mapped products section`, life: 3000 }));
                    return true
                }
                else if (!eachProduct.materialId) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add material code for product number ${index + 1} in mapped products section`, life: 3000 }));
                    return true
                }
                else if(eachProduct.materialId && eachProduct.materialId.toString()[0] === '7' && requirement.bussinessTypeId === 1){
                    dispatch(raiseToast({ severity: 'error', detail: `Can't add VMI materials in regular requirement!` }));
                    return true
                }
                /*else if (!eachProduct.companyERPCode && relatedCompany.tags?.isERPCodeMandatory){
                    dispatch(raiseToast({ severity: 'error', detail: `Company ERP code is mandatory, Please add company erp code for product number ${index + 1} in mapped products section`, life: 3000 }));
                    return true
                }*/
                return false
            })
            if (invalidMappedProduct) return

            const invalidUnmappedProducts = productsWithoutErpCode.some((eachProduct, index) => {
                if (!eachProduct.quantity || parseFloat(eachProduct.quantity) === 0) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add quantity for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct.category) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please select category for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct.materialUOMId) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please select UOM for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct.productName || !eachProduct.productName.trim()) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add product name for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (eachProduct.productName.trim().length < 3) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add atleast 3 letters in product name, product number ${index + 1}, in unmapped products section`, life: 3000 }))
                    return true
                }
                if (eachProduct.productName?.trim()?.length > 250) {
                    dispatch(raiseToast({ severity: 'error', detail: `Product name should be less than 250 characters, product number ${index + 1}- unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct?.brand?.brandId && !(typeof (eachProduct?.brand) === 'number')) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add brand for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct.specification || !eachProduct.specification.trim()) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add specification for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                if (eachProduct.specification?.trim()?.length < 3) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add atleast 3 letters in specification, product number ${index + 1}, in unmapped products section`, life: 3000 }))
                    return true
                }
                if (eachProduct.specification?.trim()?.length > 255) {
                    dispatch(raiseToast({ severity: 'error', detail: `Product specification should be less than 255 characters, product number ${index + 1}- unmapped products section`, life: 3000 }))
                    return true
                }
                if (!eachProduct.uomValue) {
                    dispatch(raiseToast({ severity: 'error', detail: `Please add uom value for product number ${index + 1} in unmapped products section`, life: 3000 }))
                    return true
                }
                /*if (!eachProduct.companyERPCode && relatedCompany.tags?.isERPCodeMandatory){
                    dispatch(raiseToast({ severity: 'error', detail: `Company ERP code is mandatory, Please add company erp code for product number ${index + 1} in unmapped products section`, life: 3000 }));
                    return true
                }*/
                if(eachProduct.materialId && eachProduct.materialId.toString()[0] === '7' && requirement.bussinessTypeId === 1){
                    dispatch(raiseToast({ severity: 'error', detail: `Can't add VMI materials in regular requirement!` }));
                    return true
                }
                return false
            })
            if (invalidUnmappedProducts) return


            //check duplicacy
            const allProducts = [...productsWithErpCode, ...productsWithoutErpCode]
            let duplicateMaterialIds = []
            let duplicateERPCodes = []
            allProducts.filter((item, index, array) => {
                const isDuplicate = array.slice(0, index).some((prevItem) => {
                    return prevItem.materialId === item.materialId
                })
                if (isDuplicate) {
                    duplicateMaterialIds.push(item.materialId)
                }

                const isERPDuplicate = array.slice(0, index).some((prevItem) => {
                    return prevItem.companyERPCode === item.companyERPCode
                })
                if (isERPDuplicate) {
                    duplicateERPCodes.push(item.companyERPCode)
                }
            })

            duplicateMaterialIds = _.compact(duplicateMaterialIds)
            if (duplicateMaterialIds.length > 0) {
                return dispatch(raiseToast({ severity: 'error', summary: 'Duplicate Material Codes', detail: `Please remove products with same material Codes. Duplicate Material Codes: ${duplicateMaterialIds.join(', ')}`, life: 3000 }))
            }

            duplicateERPCodes = _.compact(duplicateERPCodes)
            if (duplicateERPCodes.length > 0) {
                return dispatch(raiseToast({ severity: 'error', summary: 'Duplicate Client ERP Codes', detail: `Please remove products with same client ERP codes. Duplicate Client ERP Codes: ${duplicateERPCodes.join(', ')}`, life: 3000 }))
            }

            //Duplicacy check for new items
            const duplicateTempUnmappedProduct = productsWithoutErpCode.find(eachProduct => {
                const duplicateProduct = productsWithoutErpCode.find((eachUnMappedProduct, index) => {
                    if (eachProduct.productId !== eachUnMappedProduct.productId) {
                        return eachProduct.productName.trim() === eachUnMappedProduct.productName.trim()
                        && eachProduct.specification.trim() === eachUnMappedProduct.specification.trim()
                        && eachProduct.brand === eachUnMappedProduct.brand
                        && eachProduct.category === eachUnMappedProduct.category
                    }
                    return false
                })

                if (duplicateProduct) {
                    return true
                }
                return false
            })

            if (duplicateTempUnmappedProduct) {
                const productBrandId = typeof (duplicateTempUnmappedProduct.brand) === "number" ? duplicateTempUnmappedProduct.brand : duplicateTempUnmappedProduct.brand?.brandId
                const relatedBrand = brandsData.find(eachBrand => eachBrand.brandId === productBrandId)

                const productCategoryId = typeof (duplicateTempUnmappedProduct.category) === "number" ? duplicateTempUnmappedProduct.category : duplicateTempUnmappedProduct.category?.categoryId
                const relatedCategory = updatedIndustryData.find(eachCategory => eachCategory.categoryId === productCategoryId)
                return dispatch(raiseToast({ severity: 'error', detail: `Material product name: ${duplicateTempUnmappedProduct.productName}, specification: ${duplicateTempUnmappedProduct.specification}, Brand: ${relatedBrand.brandName}, Category: ${relatedCategory.categoryName} added more than once!` }))
            }


            //repetition of mapped products in unmapped products
            // let duplicateMappedProducts = false
            // if (requirement.orderTypeId === 1) {
            //     duplicateMappedProducts = productsWithoutErpCode.some(eachProduct => {
            //         if (eachProduct.companyERPCode) {
            //             const relatedCompanyERPCodeMap = companyERPCodeMaps.find(eachERP => eachERP.companyERPCode === eachProduct.companyERPCode)
            //             if (relatedCompanyERPCodeMap) {
            //                 dispatch(raiseToast({ severity: 'error', detail: `product with client ERP code ${eachProduct.companyERPCode} is a mapped product. Please move the product to mapped product section.` }))
            //                 return true
            //             }
            //         }
            //         if (eachProduct.materialId) {
            //             const relatedCompanyERPCodeMap = companyERPCodeMaps.find(eachERP => eachERP.materialId === eachProduct.materialId)
            //             if (relatedCompanyERPCodeMap) {
            //                 dispatch(raiseToast({ severity: 'error', detail: `product with material code ${eachProduct.materialId} is a mapped product. Please move the product to mapped product section.` }))
            //                 return true
            //             }
            //         }
            //         return false
            //     })
            // }
            // if (duplicateMappedProducts) return

            if (buyerReqId){
                handleIncrementalReqSubmit()
            }else {
                handleAutomateRCSubmit()
            }
        }
    }

    const handlePostRequirementSubmit = async () => {

        //ticket validations
        if(!requirement.ticketId && [1, 2].includes(requirement.orderTypeId) && requirement.reqSource === "Mail"){
            if(!requirement.emailSubject){
                return dispatch(raiseToast({ severity: 'error', detail: "Email Subject is required" }))
            }
            if(!requirement.emailRecipient){
                return dispatch(raiseToast({ severity: 'error', detail: "Email recipient is required" }))
            }
            if(!(/^\s*[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+\s*$/).test(requirement.emailRecipient)){
                return dispatch(raiseToast({ severity: 'error', detail: "Invalid email" }))
            }
            if(!requirement.emailDateStamp){
                return dispatch(raiseToast({ severity: 'error', detail: "Email date stamp is required" }))
            }
        }

        if (window.confirm('Are sure you want to submit this requirement?')) {

            let materialIds = _.compact(_.uniqBy([...productsWithErpCode, ...productsWithoutErpCode], 'materialId').map(obj => obj.materialId))

            const relatedCompany = companies.find(eachCompany => eachCompany.companyId === requirement.companyId)

            let unknownCategory = {
                categoryId: 22,
                categoryName: "Unknown",
                createdAt: "2023-08-11T10:02:08.609Z",
                createdBy: 1,
                industryName: "Procmart",
                statusId: 2,
                updatedAt: "2023-08-11T10:02:08.609Z",
                updatedBy: 1
            }
    
            let updatedIndustryData = [...industryData, unknownCategory]

            let postReqConfirmMessage = 'Are sure you want to submit this requirement?'
            setIsLoading(true)
            if (materialIds.length > 0) {
                setIsLoading(true)
                let data = {
                    companyId: requirement.companyId,
                    materialIds
                }
                let buyerPOsResponse = await getBuyerPOsByMaterialIds(data)
                if (buyerPOsResponse.failure) {
                    return dispatch(raiseToast({ severity: 'error', detail: buyerPOsResponse.data.error, life: 3000 }))
                } else {
                    if (buyerPOsResponse?.data?.length > 0) {
                        postReqConfirmMessage = "Below items have been punched for this client - please note that you will not be able to PO punch these items again with same client PO num: \n"
                        buyerPOsResponse?.data?.forEach(eachBuyerPOItem => {
                            let uniqueClientPONums = []
                            if (eachBuyerPOItem.clientPONums.length > 0) {
                                eachBuyerPOItem.clientPONums.map(eachClientPONum => {
                                    if (!uniqueClientPONums.includes(eachClientPONum)) {
                                        uniqueClientPONums.push(eachClientPONum)
                                    }
                                })
                            }
                            const clientPONums = uniqueClientPONums.join(', ');
                            postReqConfirmMessage += `MaterialID: ${eachBuyerPOItem.materialId}, Client PO Nums: ${clientPONums}\n`;
                        })

                        postReqConfirmMessage += 'Are sure you want to submit this requirement?'
                    }
                }
            }

            const activePMCompanyDetails = rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)
            requirement.pmCompanyId = activePMCompanyDetails?.pmCompanyId
            requirement.isRouted = requirement.isRouted === "Yes" ? true : false
            requirement.isTaxExempted = null
            requirement.ticketId = requirement.ticketId ? requirement.ticketId : null
            if (relatedCompany.tags?.hasRCTaxExemption){
                requirement.isTaxExempted = requirement.isTaxExempted === "Yes" ? true : false
            }

            let isURP = false
            const selectedBillAddr = billingAddresses.find(eachBillAddr => eachBillAddr.billAddrId === requirement.billAddrId)
            if (selectedBillAddr) {
                if (selectedBillAddr.gstinCode === "URP") {
                    isURP = true
                }
            }
            requirement.isURP = isURP

            let productsWithERPCodeMap = []
            if (productsWithErpCode.length > 0) {
                productsWithERPCodeMap = productsWithErpCode.map(eachProduct => ({
                    materialId: eachProduct.materialId,
                    qty: parseFloat(eachProduct.quantity),
                    quantity: parseFloat(eachProduct.quantity),
                    buyerRateContractId: eachProduct.buyerRateContractId,
                    companyERPCodeMapId: eachProduct?.companyERPCodeMapId ? eachProduct?.companyERPCodeMapId : null,
                    productId: eachProduct?.productId,
                    companyERPCode: eachProduct?.companyERPCode ? eachProduct?.companyERPCode : null,
                    priority: eachProduct.isHighPriority ? 1 : 3,
                    existedQuantity: eachProduct.existedQuantity
                }))
            }

            let productsWithoutERPCodeMap = []
            if (productsWithoutErpCode.length > 0) {
                productsWithoutERPCodeMap = productsWithoutErpCode.map(eachProduct => {
                    const productBrandId = typeof (eachProduct.brand) === "number" ? eachProduct.brand : eachProduct.brand?.brandId
                    const productCategoryId = typeof (eachProduct.category) === "number" ? eachProduct.category : eachProduct.category?.categoryId
                    const relatedBrand = brandsData.find(eachBrand => eachBrand.brandId === productBrandId)

                    const companyERPUOM = uomsData.find(eachUOM => eachUOM.materialUOMId === eachProduct.materialUOMId)
                    const companyERPCategory = updatedIndustryData.find(eachCategory => eachCategory.categoryId === productCategoryId)

                    return {
                        productId: eachProduct?.productId,
                        companyId: requirement.companyId,
                        companyERPCode: eachProduct?.companyERPCode ? eachProduct?.companyERPCode : null,
                        materialId: eachProduct?.materialId,
                        companyERPCategory: companyERPCategory.categoryName,
                        companyERPUOM: companyERPUOM.UOM,
                        companyERPProductName: getProductName(relatedBrand?.brandName, eachProduct.productName, eachProduct?.variantName),
                        productName: eachProduct.productName,
                        variantName: eachProduct?.variantName ? eachProduct?.variantName : null,
                        specification: eachProduct.specification,
                        productSpecifications: eachProduct.specification,  //remove extras
                        uomValue: eachProduct.uomValue,
                        brandId: productBrandId,
                        categoryId: productCategoryId,
                        buyerRateContractId: eachProduct?.buyerRateContractId,
                        UOM: eachProduct.materialUOMId,  //remove extras
                        UOMValue: eachProduct.uomValue, //remove extras
                        qty: parseFloat(eachProduct.quantity),
                        quantity: parseFloat(eachProduct.quantity),
                        companyERPCodeMapId: eachProduct?.companyERPCodeMapId ? eachProduct.companyERPCodeMapId : null,
                        priority: eachProduct.isHighPriority ? 1 : 3,
                        existedQuantity: eachProduct.existedQuantity
                    }
                })
            }
        
            let addBuyerReqRes = null
            if (buyerReqId){
                let clientPONum = incrementalReqLogs?.length ? incrementalReqLogs[0].clientPONum : buyerPO.clientPONum
                addBuyerReqRes = await insertIncrementalReqLog({ bulkReqObj: {...requirement, clientPONum: clientPONum}, productItems: { productsWithERPCodeMap, productsWithoutERPCodeMap }, existingIncrementalReqLogs: incrementalReqLogs })
            }else {
                addBuyerReqRes = await insertBuyerRequirement({ bulkReqObj: requirement, productItems: { productsWithERPCodeMap, productsWithoutERPCodeMap }, isPostRequirementTransactionsEnabled: PMCompanyConfig.isPostRequirementTransactionsEnabled })
            }

            if (addBuyerReqRes.failure) {
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', summary: 'Submit Post Requirement', detail: addBuyerReqRes.data.error, life: 3000 }))
            } else {
                let newBuyerReq = addBuyerReqRes?.data?.buyerReq
                newBuyerReq = { ...newBuyerReq, 'kam.firstName': userNameDetails.firstName, 'kam.lastName': userNameDetails.lastName, 'pmCompanyBranch.branchName': activeRole?.branchName }

                let buyerComapnyDetails = await getCompanyById(newBuyerReq.companyId)
                if (!buyerComapnyDetails.failure) {
                    buyerComapnyDetails = buyerComapnyDetails?.data
                    const relatedBuyerCompanyBranch = buyerComapnyDetails?.companyBranches.find(each => each.branchId === newBuyerReq?.branchId)
                    newBuyerReq = { ...newBuyerReq, 'company.companyName': buyerComapnyDetails?.companyName, 'buyerCompanyBranch.branchName': relatedBuyerCompanyBranch?.branchName, "buyerCompanyBranch.clientSuccessManagerId": relatedBuyerCompanyBranch?.clientSuccessManagerId }
                }

                let buyerUserDetails = await getUserById(newBuyerReq?.userId)
                if (!buyerUserDetails.failure) {
                    buyerUserDetails = buyerUserDetails?.data?.[0]
                    newBuyerReq = { ...newBuyerReq, 'buyer.firstName': buyerUserDetails?.firstName, 'buyer.lastName': buyerUserDetails?.lastName }
                }
                const newBuyerReqItems = addBuyerReqRes?.data?.buyerReqItems
                dispatch(updateBuyerRequirementData([newBuyerReq, ...buyerRequirementData]))
                dispatch(updateBuyerRequirementItemsData([...newBuyerReqItems, ...buyerRequirementItemsData]))
            }

            setFinished()
            setIsLoading(false)
            navigate('/requirement')
        }
    }

    const productsWithErpCode = products.filter(eachProduct => eachProduct.isMappedProduct)
    const productsWithoutErpCode = products.filter(eachProduct => !eachProduct.isMappedProduct)

    const uploadOptions = requirement.orderTypeId === 2 ? uploadRCSheet : uploadADHOCSheet

    let showAddProduct = false
    if (requirement.orderTypeId === 1 && requirement.companyId) {
        showAddProduct = true
    }
    else if (requirement.orderTypeId === 3 && requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 1) {
        showAddProduct = true
    }

    let fileUploadDisableCond = true
    if (requirement.orderTypeId === 1) {
        if (requirement.companyId && requirement.bussinessTypeId === 1 && isCompanyERPCodeMapsLoading === false) {
            fileUploadDisableCond = false
        }
        else if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 2 && requirement.pmWarehouseId && isCompanyERPCodeMapsLoading === false){
            fileUploadDisableCond = false
        }
    }
    else if (requirement.orderTypeId === 2) {
        if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 1 && isRateContractProductsLoading === false) {
            fileUploadDisableCond = false
        }
        else if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 2 && isRateContractProductsLoading === false && requirement.pmWarehouseId) {
            fileUploadDisableCond = false
        }
    }
    
    const selectedCompany = companies.find(eachCompany => eachCompany.companyId === requirement.companyId)
    if (selectedCompany){
        if (selectedCompany.tags?.hasRCTaxExemption){
            if (requirement.isTaxExempted){
                fileUploadDisableCond = false
            }else{
                fileUploadDisableCond = true
            }
        }
    }


    let addMaterialSearchDisableCond = true
    if (requirement.orderTypeId === 1) {
        if (requirement.companyId && requirement.bussinessTypeId === 1 && isCompanyERPCodeMapsLoading === false) {
            addMaterialSearchDisableCond = false
        }
        else if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 2 && requirement.pmWarehouseId && isCompanyERPCodeMapsLoading === false){
            addMaterialSearchDisableCond = false
        }
    }
    else if (requirement.orderTypeId === 3) {
        if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 1) {
            addMaterialSearchDisableCond = false
        }
        else if (requirement.companyId && requirement.branchId && requirement.bussinessTypeId === 2){
            addMaterialSearchDisableCond = false
        }
    }
    
    const bgColorVMI = requirement.bussinessTypeId === 2 ? `bg-green-50` : ''

    let message = "This req will be considered as incremental Req"
    
    return (
        <>
            <Loading visible={isLoading} />
            <div className='post-req-container'>
                <h4 className='m-2'>POST REQUIREMENT</h4>
                {
                    buyerReqId ? 
                    <Messages severity="warn" messages={message} sticky={true}/>
                    :
                    null
                }
                <RequirementDetails
                    buyerReqId={buyerReqId}
                    requirement={requirement}
                    orderTypes={orderTypes}
                    bussinessTypes={bussinessTypes}
                    handleBussinessType={handleBussinessType}
                    handleOrderType={handleOrderType}
                    formErrors={formErrors}
                    companySearchMethod={companySearchMethod}
                    companySuggestions={companySuggestions}
                    handleCompanyChange={handleCompanyChange}
                    isCompanyDataLoading={isCompanyDataLoading}
                    companies={companies}
                    branches={branches}
                    gstins={gstins}
                    users={users}
                    warehouses={warehouses}
                    pmBranches={pmBranches}
                    handleBranchChange={handleBranchChange}
                    handleGstinChange={handleGstinChange}
                    handleUserChange={handleUserChange}
                    handlePMWarehouse={handlePMWarehouse}
                    handlePMBranch={handlePMBranch}
                    isBranchDataLoading={isBranchDataLoading}
                    isGSTINSDataLoading={isGSTINSDataLoading}
                    isUsersDataLoading={isUsersDataLoading}
                    handleRoutedChange={handleRoutedChange}
                    handleReqTagChange={handleReqTagChange}
                    handleReqSource={handleReqSource}
                    handleTaxExemption={handleTaxExemption}
                    handleRequirementCreatedTime={handleRequirementCreatedTime}
                    handleSourceDocUpload={handleSourceDocUpload}
                    removeSourceDocument={removeSourceDocument}
                    uploadOptions={uploadOptions}
                    fileUploadDisableCond={fileUploadDisableCond}
                    handlProductsUploadFile={handlProductsUploadFile}
                    bgColorVMI={bgColorVMI}
                    reqTags={reqTags}
                    PMCompanyConfig={PMCompanyConfig}
                    pendingReqList={pendingReqList}
                    handleClientReqNoChange={handleClientReqNoChange}
                    handleRequirementChange={handleRequirementChange}
                />
                <div className="p-card pb-1">
                    <ProductsSection
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        requirement={requirement}
                        productsWithErpCode={productsWithErpCode}
                        productsWithoutErpCode={productsWithoutErpCode}
                        handleProductChange={handleProductChange}
                        handleRemoveProduct={handleRemoveProduct}
                        brandsData={brandsData}
                        uomsData={uomsData}
                        industryData={industryData}
                        handleAutoFillProductData={handleAutoFillProductData}
                        handleMoveToSectiontwo={handleMoveToSectiontwo}
                        handleActiveTab={handleActiveTab}
                        companyERPCodeMaps={companyERPCodeMaps}
                        setIsLoading={setIsLoading}
                        products={products}
                        setProducts={setProducts}
                        showAddProduct={showAddProduct}
                        handleAddProductWithErpCode={handleAddProductWithErpCode}
                        fileUploadDisableCond={fileUploadDisableCond}
                        addMaterialSearchDisableCond={addMaterialSearchDisableCond}
                        handleUnmappedProductsMoves={handleUnmappedProductsMoves}
                        handleAddProductWithoutErpCode={handleAddProductWithoutErpCode}
                        handleRemoveAllSec1Products={handleRemoveAllSec1Products}
                        setVisisbleAddRCProducts={setVisisbleAddRCProducts}
                        setVisisbleAddAdhocProducts={setVisisbleAddAdhocProducts}
                        handleRemoveAllSec2Products={handleRemoveAllSec2Products}
                        bgColorVMI={bgColorVMI}
                        buyerReqId={buyerReqId}
                    />
                </div>
                <AddressSelection
                    requirement={requirement}
                    billingAddresses={billingAddresses}
                    handleBillingAddress={handleBillingAddress}
                    shippingAddresses={shippingAddresses}
                    handleShippingAddress={handleShippingAddress}
                    setVisibleAddNewShippingAddr={setVisibleAddNewShippingAddr}
                    bgColorVMI={bgColorVMI}
                    buyerReqId={buyerReqId}
                    userDetails={userDetails}
                />
                <div className="flex justify-content-center mt-2 mb-3">
                    <Button label="Post Requirement" onClick={handlePostRequirementValidations} permissions={[5]} />
                </div>
                <Dialog header="Add New Company" className='add-new-company-dialog' visible={visibleAddNewCompany} onHide={() => setVisibleAddNewCompany(false)}>
                    <AddNewCompany
                        pinCodesData={pinCodes}
                        stateList={states}
                        requirement={requirement}
                        companies={companies}
                        handleAddNewCompanySubmit={handleAddNewCompanySubmit}
                        setIsLoading={setIsLoading} setBranches={setBranches}
                        setCompanies={setCompanies} setGstins={setGstins}
                        setUsers={setUsers}
                        setShippingAddressesList={setShippingAddressesList}
                        setBillingAddressesList={setBillingAddressesList}
                        setRequirement={setRequirement}
                        setVisibleAddNewCompany={setVisibleAddNewCompany}
                        countriesData={countriesData}
                    />
                </Dialog>
                <Dialog header="Add RC Products" className="add-rc-product-dialog" visible={visisbleAddRCProducts} onHide={() => setVisisbleAddRCProducts(false)} style={{ width: '', height: '80vh' }}>
                    <RateContractSearch rateContractProducts={rateContractProducts} products={products} setProducts={setProducts} company={selectedCompany} requirement={requirement}/>
                </Dialog>
                <Dialog header="Add Products" className="add-rc-product-dialog" visible={visisbleAddAdhocProducts} onHide={() => setVisisbleAddAdhocProducts(false)} style={{ width: '', height: '80vh' }}>
                    <MaterialSearch companyERPCodeMaps={companyERPCodeMaps} uomsData={uomsData} products={products} setProducts={setProducts} companyId={requirement.companyId} requirement={requirement} />
                </Dialog>
                <Dialog header="Add New Shipping Address" className='add-new-shipping-address-dialog'  visible={visibleAddNewShippingAddr} onHide={() => setVisibleAddNewShippingAddr(false)}>
                    <AddShippingAddress stateList={states} setVisibleAddNewShippingAddr={setVisibleAddNewShippingAddr} requirement={requirement} setShippingAddressesList={setShippingAddressesList} shippingAddressesList={shippingAddresses} handleSubmit={handleAddShippingAddressSubmit} countriesData={countriesData} pinCodesData={pinCodes} />
                </Dialog>
                <Dialog header="LONG PENDING SCHEDULE ITEMS" className='add-pending-req'  visible={visiblePendingReqModel}  style={{ width: '50vw', height: '40vh' }} onHide={() => setVisiblePendingReqModel(false)}>
                    <PendingBuyerReq pendingReqList={pendingReqList}/>
                </Dialog>
                <Dialog header="" visible={visibleAutomateRCModal} style={{ width: '30vw', height: !requirement.ticketId && requirement.reqSource === "Mail" ? '54vh' : '22vh' }} onHide={handleAutomateRCOnHide} footer={renderAutomateRCFooter}>
                    <div>
                        {
                            !requirement.ticketId && requirement.reqSource === "Mail" ?
                                <>
                                    <EmailRecipientDetails requirement={requirement} handleRequirementChange={handleRequirementChange} />
                                </>
                                : null
                        }
                        <div className='flex align-items-center pt-5'>
                            <CheckboxField name="isRCAutomated" checked={requirement.isRCAutomated} value={requirement.isRCAutomated} onChange={handleAutomateRC} />
                            <span className="ml-2">Proceed with Dummy Seller Quotes</span>
                        </div>
                    </div>
                </Dialog>
                <Dialog header="" visible={visibleAdhocEmailRecipientDetails} style={{ width: '30vw', height: '50vh' }} onHide={handleAdhocEmailRecipientDetailsOnHide} footer={renderAdhocEmailRecipientDetails}>
                    <>
                        <EmailRecipientDetails requirement={requirement} handleRequirementChange={handleRequirementChange} />
                    </>
                </Dialog>
                <Dialog header="Incremental Requirement Details" visible={isIncrementalReqConfirmModalVisible} style={{ width: '90vw', minHeight: "50vh"}} footer={renderIncrementalReqFooter} onHide={() => {setIsIncrementalReqConfirmModalVisible(false); setIncrementalReqProducts([])}}>
                    <ReqDetailsModal buyerReqDisplayProducts={incrementalReqProducts} isIncrementalReq={buyerReqId ? true : false}/>
                </Dialog>
            </div>
        </>
    )
}

export default PostRequirement;
